import React, { useEffect, useState, useRef } from "react";
import { NavLink, Redirect } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { Device } from "@twilio/voice-sdk";
import he from "he";
import moment from "moment-timezone";
import LogRocket from "logrocket";

import ring from "../../../assets/audio/ring.mp3";
import warning_sound_audio from "../../../assets/audio/warning.mp3";
import end_sound_audio from "../../../assets/audio/end.mp3";
import beep_sound_audio from "../../../assets/audio/beep.mp3";

import Icon from "../../../components/main/icon";
import Textarea from "../../../components/main/textarea";
import Input from "../../../components/main/input";
import Select from "../../../components/main/select";
import Button from "../../../components/main/button";
import Loader from "../../../components/main/loader";

import Misc from "../../../utils/misc";
import fetch from "../../../utils/fetch";

import { useUserDataStore } from "../../../store/userData";
import { useModalDataStore } from "../../../store/modalData";
import { useUpdateDataStore } from "../../../store/updateData";
import { useSocketDataStore } from "../../../store/socketData";
import { useActiveClientIdStore } from "../../../store/activeClientId";
import { useActiveClientStore } from "../../../store/activeClient";
import { useClientsDataStore } from "../../../store/clientsData";
import { useShowErrorFlashStore } from "../../../store/showErrorFlash";
import { useShowWarningFlashStore } from "../../../store/showWarningFlash";
import { useStatusStore } from "../../../store/statusData";

LogRocket.init("twr8jc/vca");

interface Answer {
    id: number;
    text: string;
    next_questions?: Question[];
}

interface Question {
    id: number;
    type: "input" | "textarea" | "select" | "checkbox" | "radio";
    text: string;
    section?: string;
    information?: string;
    answers?: Answer[];
    informationStyle?: any;
    sectionStyle?: any;
    next_questions?: Question[];
}

const _he_rounds = (text: string, rounds: number = 20) => {
    if (text === "") return text;
    let i = 0;
    while (i < rounds) {
        text = he.decode(text || "");
        i++;
    }

    return text;
};

const ConditionalForm: React.FC<any> = ({ form, update_answers, update_full_answers, update_current_question_contacts, clear = false }) => {
    const [answers, setAnswers] = useState<any>([]);
    const [currentQuestions, setCurrentQuestions] = useState<any>([]);
    const [fullQuestionAnswers, setFullQuestionAnswers] = useState<any>([]);

    const handleAnswer = (questionIndex: number, answerValue: string, question: Question, nextQuestions?: Question[], multi: any = {}) => {
        // Update answers array based on the current question index
        let updatedAnswers;
        if (Object.keys(multi).length > 0) {
            let new_value: any = JSON.stringify([]);

            if (multi?.checked === true) {
                new_value = JSON.parse(answers?.[questionIndex] || "[]");
                new_value.push(answerValue);
            } else {
                if (multi?.checked === true) {
                    new_value = JSON.parse(answers?.[questionIndex] || "[]");
                    new_value.push(answerValue);
                } else if (JSON.parse(answers?.[questionIndex] || "[]").length > 0 && multi?.checked === false) {
                    new_value = JSON.parse(new_value);
                    for (let i = 0; i < JSON.parse(answers?.[questionIndex] || "[]").length; i++) {
                        if (JSON.parse(answers?.[questionIndex] || "[]")[i] !== answerValue) new_value.push(JSON.parse(answers?.[questionIndex] || "[]")[i]);
                    }
                }
            }

            updatedAnswers = [...answers.slice(0, questionIndex), JSON.stringify(new_value)];
        } else if (["input", "textarea"].includes(question.type)) {
            // since input/textarea questions don't affect branching logic
            updatedAnswers = [
                ...answers.slice(0, questionIndex),
                answerValue,
                ...answers.slice(questionIndex + 1), // ← preserve everything after
            ];
        } else {
            updatedAnswers = [...answers.slice(0, questionIndex), answerValue];
        }

        setAnswers(updatedAnswers);

        // For input and textarea types, check if there are next questions in the first answer
        let nxtQuestions = nextQuestions;
        if (["input", "textarea"].includes(question.type) && !nxtQuestions && question.answers && question.answers.length > 0) {
            nxtQuestions = question.answers[0].next_questions;
        }

        // Check for next questions and log the relevant data
        if (nxtQuestions && nxtQuestions.length > 0) {
            setCurrentQuestions([...currentQuestions.slice(0, questionIndex + 1), ...nxtQuestions]);
        } else if (["select", "radio", "checkbox"].includes(question.type)) {
            // For select/radio/checkbox with no next questions, remove any previously added sub-questions
            // Find the next top-level question index
            const nextTopLevelIndex = currentQuestions.findIndex((q: any, idx: any) => idx > questionIndex && form?.questions?.some((topQ: any) => topQ.id === q.id));

            if (nextTopLevelIndex > -1) {
                // Keep up to current question, remove sub-questions, keep remaining top-level questions
                setCurrentQuestions([...currentQuestions.slice(0, questionIndex + 1), ...currentQuestions.slice(nextTopLevelIndex)]);
            } else {
                // No more top-level questions, just remove sub-questions
                setCurrentQuestions(currentQuestions.slice(0, questionIndex + 1));
            }
        }

        /* if (nxtQuestions) {
            console.log("Next Questions:", nxtQuestions);
            // Set the current questions to include the selected question and next questions
            setCurrentQuestions([...currentQuestions.slice(0, questionIndex + 1), ...nxtQuestions]);
        } else {
            // Log the current questions up to the selected question
            console.log("No Next Questions, keeping current:", currentQuestions.slice(0, questionIndex + 1));
            // If no next questions, keep the current questions up to the selected one
            setCurrentQuestions(currentQuestions.slice(0, questionIndex + 1));
        } */
    };

    const renderQuestion = (question: Question, qIndex: number) => {
        let extra = "";
        if (question?.section && _he_rounds(question?.section).replace(/<p>|<\/p>/g, "") !== "undefined" && _he_rounds(question?.section) !== "<p><br></p>") extra += `<div>${_he_rounds(question?.section || "")}</div>`;
        if (question?.information && _he_rounds(question?.information).replace(/<p>|<\/p>/g, "") !== "undefined" && _he_rounds(question?.information) !== "<p><br></p>") extra += `<div>${_he_rounds(question?.information || "-")}</div>`;

        switch (question?.type) {
            case "select":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>
                        <p dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></p>
                        <div className="input-small">
                            <select
                                onChange={(e) => {
                                    const selectedAnswer = question.answers ? question.answers[e.target.selectedIndex - 1] : undefined;
                                    if (selectedAnswer) {
                                        handleAnswer(qIndex, selectedAnswer.text, question, selectedAnswer.next_questions);
                                    }
                                }}>
                                <option value="">Select an option</option>
                                {(question?.answers || []).map((answer) => (
                                    <option key={answer.id} value={answer.text}>
                                        {answer.text}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                );
            case "input":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>

                        <div dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></div>
                        <Input
                            type="text"
                            name={question.id + ""}
                            placeholder={""}
                            className="input-small"
                            container={false}
                            defaultValue={answers?.[qIndex]}
                            updateChange={(v) => handleAnswer(qIndex, v, question, question.next_questions)}
                            changeDefault={new Date().getTime()}
                        />
                    </div>
                );
            case "textarea":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>

                        <p dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></p>

                        <Textarea name="" placeholder={""} defaultValue={answers?.[qIndex]} updateChange={(v) => handleAnswer(qIndex, v, question, question.next_questions)} />
                    </div>
                );
            case "checkbox":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>

                        <p dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></p>
                        {(question?.answers || []).map((answer) => (
                            <div key={answer.id}>
                                <label>
                                    <input type="checkbox" value={answer.text} onChange={(e) => handleAnswer(qIndex, answer.text, question, answer.next_questions, { checked: e.target.checked })} />
                                    <span className="ml-2" style={{ verticalAlign: "middle" }}>
                                        {answer.text}
                                    </span>
                                </label>
                            </div>
                        ))}
                    </div>
                );
            case "radio":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>

                        <p dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></p>
                        {(question?.answers || []).map((answer) => (
                            <div key={answer.id}>
                                <input type="radio" name={question.text} value={answer.text} onChange={(e) => handleAnswer(qIndex, answer.text, question, answer.next_questions)} />
                                <span className="ml-2" style={{ verticalAlign: "middle" }}>
                                    {answer.text}
                                </span>
                            </div>
                        ))}
                    </div>
                );
            default:
                return null;
        }
    };

    // useEffect(() => {
    //     let fullData: any = [];

    //     (answers || []).map((answer: any, index: any) => {
    //         const questionJson = currentQuestions?.[index];
    //         if (questionJson && answer) {
    //             // Only include if both question and answer exist
    //             fullData.push({
    //                 question: questionJson, // Full question object
    //                 answer: answer,
    //             });
    //         }
    //     });

    //     setFullQuestionAnswers(fullData);
    // }, [answers, currentQuestions]);

    useEffect(() => {
        let a: any = [];
        let fullData: any = [];
        {
            (answers || []).map((answer: any, index: any) => {
                a.push({
                    question: currentQuestions?.[index]?.text,
                    answer: answer,
                });

                const questionJson = currentQuestions?.[index];
                if (questionJson && answer) {
                    fullData.push({
                        question: currentQuestions?.[index]?.text, // Question text
                        answer: answer, // User's actual response only (same as above)
                        ...questionJson, // Spread ALL other question properties (id, type, section, information, etc.)
                    });
                }
            });
        }

        update_answers(a);
        setFullQuestionAnswers(fullData);
    }, [answers]);

    useEffect(() => {
        if (form) setCurrentQuestions(form?.questions || []);
        // if (form) setCurrentQuestions([form?.questions?.[0]]);
    }, [form]);

    useEffect(() => {
        if (clear === true && JSON.stringify(answers) !== "[]") {
            setAnswers([]);
            setCurrentQuestions([form?.questions?.[0]]);
            setFullQuestionAnswers([]); // NEW: Clear the full data too
        }
    }, [clear]);

    useEffect(() => {
        if (update_full_answers) {
            update_full_answers(fullQuestionAnswers);
        }
    }, [fullQuestionAnswers]);

    useEffect(() => {
        if (update_current_question_contacts && currentQuestions.length > 0) {
            const currentQuestion = currentQuestions[currentQuestions.length - 1];
            update_current_question_contacts(currentQuestion?.contacts || []);
        }
    }, [currentQuestions]);

    if (Number(form?.status || 0) !== 1) return <></>;

    return (
        <div className="col-12">
            <hr />
            {/* <h3>Conditional Form</h3> */}
            {(currentQuestions || []).map((question: any, qIndex: number) => renderQuestion(question, qIndex))}
            {/* <div className="mt-4">
                <h3>Your Answers:</h3>
                {(answers || []).map((answer: any, index: any) => (
                    <div key={index}>
                        <span>{currentQuestions?.[index]?.text}</span>
                        <p key={index}>{answer}</p>
                    </div>
                ))}
            </div> */}
        </div>
    );
};

interface Props {}

const Home: React.FC<Props> = () => {
    const user = useUserDataStore<any>((s) => s.user);
    const clients = useClientsDataStore<any>((s) => s.clients);
    const setShowErrorFlash = useShowErrorFlashStore<any>((s) => s.setShowErrorFlash);
    const setShowWarningFlash = useShowWarningFlashStore<any>((s) => s.setShowWarningFlash);
    const modalData = useModalDataStore<any>((s) => s.modalData);
    const setModalData = useModalDataStore<any>((s) => s.setModalData);
    const socketCallQueue = useSocketDataStore<any>((s) => s.socketCallQueue);
    const socketCallStatus = useSocketDataStore<any>((s) => s.socketCallStatus);
    const bhvmTickets = useSocketDataStore<any>((s) => s.bhvmTickets);
    const setActiveClientId = useActiveClientIdStore<any>((s) => s.setActiveClientId);
    const activeClientId = useActiveClientIdStore<any>((s) => s.activeClientId);
    const activeClientIdRef = useRef<any>(activeClientId);
    const setActiveClient = useActiveClientStore<any>((s) => s.setActiveClient);
    const activeClient = useActiveClientStore<any>((s) => s.activeClient);
    const activeClientRef = useRef<any>(activeClient);
    const setStatus = useStatusStore<any>((s) => s.setStatus);
    const status = useStatusStore<any>((s) => s.status); // '', inbound, ringing, search, outbound, wrapping up
    const statusRef = useRef<any>(status);
    const [toggleAdminSection, setToggleAdminSection] = useState<boolean>(false);
    const [searchContacts, setSearchContacts] = useState<string>("");
    const [createCustomerAlert, setCreateCustomerAlert] = useState<string>("");
    const [sendMessage, setSendMessage] = useState<any>({});
    const [activeTransfer, setActiveTransfer] = useState<number>(-1);
    const [conditionalFormAnswers, setConditionalFormAnswers] = useState<any>([]);
    const [conditionalFormAnswersFull, setConditionalFormAnswersFull] = useState<any>([]);
    const [conditionalForm, setConditionalForm] = useState<any>(null);
    const [conditionalFormView, setConditionalFormView] = useState<any>(null);
    const [outboundCall, setOutboundCall] = useState<any>({ caller_prefix: "+61" });
    const inboundCallRef = useRef<any>({});
    const outboundCallRef = useRef<any>({ caller_prefix: "+61" });
    const [customerAlerts, setCustomerAlerts] = useState<any>([]);
    const [timerLongestInQueue, setTimerLongestInQueue] = useState<string>("00:00");
    const [timerWrapping, setTimerWrapping] = useState<any>({ ts: 0, string: "00:00" });
    const [timerCallDuration, setTimerCallDuration] = useState<any>({ ts: 0, string: "00:00" });
    const [timerAgentCallDuration, setTimerAgentCallDuration] = useState<any>({ ts: 0, string: "00:00" });
    const [timerClientCallDuration, setTimerClientCallDuration] = useState<any>({ ts: 0, string: "00:00" });
    const [showKeyPad, setShowKeyPad] = useState<any>(false);
    const [messageSent, setMessageSent] = useState<any>(false);
    const [twilioDevice, setTwilioDevice] = useState<any>(null);
    const [currentQuestionContacts, setCurrentQuestionContacts] = useState<string[]>([]);
    const [endCallSeconds, setEndCallSeconds] = useState<number>(0);

    const ringing = useRef(new Audio(ring));
    const warning_sound = useRef(new Audio(warning_sound_audio));
    const end_sound = useRef(new Audio(end_sound_audio));
    const beep_sound = useRef(new Audio(beep_sound_audio));
    ringing.current.loop = true;

    const [buttonActive, setButtonActive] = useState<any>({
        save_alert: false,
        send_message: false,
        check_customer_alerts: false,
        call_action: {},
    });

    const buttonActiveRef = useRef<any>({
        save_alert: false,
        send_message: false,
        check_customer_alerts: false,
        call_action: {},
    });

    let vca_left_body = (ref: any, extra: any[] = []) => {
        if (!ref) return;
        const resizeObserver = new ResizeObserver(() => {
            // @ts-ignore
            const headerHeight = (extra || []).map((id) => document.querySelector(id)?.offsetHeight || 0).reduce((a, b) => a + b, 0);
            // @ts-ignore
            ref.style.height = `${document.getElementById("vca-left")?.offsetHeight - headerHeight}px`;
        });
        // @ts-ignore
        extra.forEach((id) => resizeObserver.observe(document.querySelector(id)));
    };

    let vca_right_contact = (ref: any) => {
        if (!ref) return;
        const resizeObserver = new ResizeObserver(() => {
            // @ts-ignor-e
            const headerHeight = ["#vca-right-phone-inbound", "#vca-right-phone-outbound"]
                .map((id) => {
                    const _a: any = document.querySelector(id);
                    if (_a) return _a.offsetHeight || 0;
                })
                .reduce((a: any, b: any) => a + b, 0);
            // const headerHeight = ["#vca-right-phone-inbound", "#vca-right-phone-outbound"].map((id) => document.querySelector(id)?.offsetHeight || 0).reduce((a, b) => a + b, 0);
            // @ts-ignor-e
            const _a = document.getElementById("vca-left");
            if (_a) ref.style.height = `${_a.offsetHeight - headerHeight - 60}px`;
        });
        // @ts-ignore
        ["#vca-right-phone-inbound", "#vca-right-phone-outbound"].forEach((id) => resizeObserver.observe(document.querySelector(id)));
    };

    const send_message_state_prep = () => {
        if (modalData.type === "location search") return;
        if (activeClientId !== 0) {
            let obj: any = {};
            for (let i = 0; i < (clients || []).length; i++) {
                if (Number(clients[i].id) === activeClientId) {
                    obj = clients[i];
                    break;
                }
            }
            setActiveClient(obj);
            const _form = (obj?.forms || []).length > 0 ? obj?.forms[0].form.form.form || [] : [];
            let _custom: any = [];
            for (let i = 0; i < _form.length; i++) {
                _custom.push({
                    [_form[i].label]: {
                        type: _form[i].type,
                        value: _form[i].type === "multiplechoice" ? [] : "",
                    },
                });
            }
            let caller_number = inboundCallRef.current.from || outboundCall.to || "";
            for (let index = 0; index < (obj.dids || []).length; index++) {
                if ((obj.dids || [])[index].did === inboundCallRef.current.did && Number((obj.dids || [])[index].hide_cli) === 1) {
                    caller_number = "";
                    break;
                }
            }

            setSendMessage({
                account_id: Number(activeClientId),
                custom: _custom,
                source: "email",
                conf_sid: inboundCallRef.current.conference_sid || outboundCall.conference_sid || null,
                caller_number: caller_number,
                caller_call_sid: inboundCallRef.current.participant_caller_call_sid || outboundCall.participant_caller_call_sid || "",
            });
            setConditionalFormAnswers([]);
            setCustomerAlerts([]);

            const params = new URLSearchParams(window.location.search);
            const start_call_did = params.get("start_call_did")?.replace(/ /g, "+");
            const start_call_cli = params.get("start_call_cli")?.replace(/ /g, "+").replace("+61", "");
            const start_call_sid = params.get("start_call_sid");

            if (start_call_did && start_call_cli) {
                params.delete("start_call_did");
                params.delete("start_call_cli");
                const newUrl = window.location.pathname;
                window.history.replaceState(null, "", newUrl + "?bhvm_sid=" + start_call_sid);
                make_outbound("false", { caller_prefix: "+61", did: start_call_did ? start_call_did : outboundCall.did, destination: start_call_cli ? start_call_cli : outboundCall.destination }, true);

                //     setOutboundCall({ ...outboundCall, caller_prefix: "+61", did: start_call_did ? start_call_did : outboundCall.did, destination: start_call_cli ? start_call_cli : outboundCall.destination });
                // outboundCallRef.current = {
                //     ...outboundCall,
                //     caller_prefix: "+61",
                //     did: start_call_did ? start_call_did : outboundCallRef.current.did,
                //     destination: start_call_cli ? start_call_cli : outboundCallRef.current.destination,
                // };
            }

            const _a = document.getElementById("vca-right-contacts");
            if (_a) _a.scrollTop = 0;
            const _b = document.getElementById("vca-left-message");
            if (_b) _b.scrollTop = 0;
            const _c = document.getElementById("vca-left-booking");
            if (_c) _c.scrollTop = 0;
            const _d = document.getElementById("vca-left-info");
            if (_d) _d.scrollTop = 0;
        }
    };

    const save_alert = async () => {
        setButtonActive({ ...buttonActive, save_alert: true });

        const post = new fetch(
            "/user/vca/create/alert",
            {
                account_id: activeClientId,
                customer_uuid: sendMessage?.customer_uuid || "",
                alert: createCustomerAlert || "",
                caller_fn: sendMessage.caller_first_name || "",
                caller_ln: sendMessage.caller_last_name || "",
                caller_number: outboundCall.destination || "",
                caller_prefix: outboundCall.caller_prefix || "",
            },
            { success: true, error: true },
        ).post();

        post.then((res) => {
            if (res.status === "error" || res === false) {
                setShowErrorFlash(true);
                setTimeout(() => {
                    setShowErrorFlash(false);
                }, 500);
            }

            setButtonActive({ ...buttonActive, save_alert: false });
        }).catch((e) => {
            setButtonActive({ ...buttonActive, save_alert: false });
        });
    };

    const send_message = async () => {
        if (buttonActive.send_message) return;

        try {
            setButtonActive({ ...buttonActive, send_message: true });

            const post = new fetch(
                "/user/vca/create/messages",
                {
                    ...sendMessage,
                    conditional_form: JSON.parse(_he_rounds(JSON.stringify(conditionalFormAnswers))) || [],
                    conditional_form_full: JSON.parse(_he_rounds(JSON.stringify(conditionalFormAnswersFull))) || [],
                    caller_call_sid: inboundCallRef.current.participant_caller_call_sid || outboundCallRef.current.participant_caller_call_sid || "",
                    outbound_agent_call_sid: outboundCallRef.current.participant_agent_call_sid || "",
                },
                { success: true, error: true },
            ).post();

            post.then((res) => {
                if (res.status === "error" || res === false) {
                    setShowErrorFlash(true);
                    setTimeout(() => {
                        setShowErrorFlash(false);
                    }, 500);
                }
                if (res.status === "success") {
                    send_message_state_prep();
                    setMessageSent(true);
                }
                setButtonActive({ ...buttonActive, send_message: false });
            }).catch((e) => {
                setButtonActive({ ...buttonActive, send_message: false });
            });
        } catch (e) {
            alert("Error sending message");
            setButtonActive({ ...buttonActive, send_message: false });
        }
    };

    const check_customer_alerts = async (phone_number: string, account_id: string | number = activeClientId) => {
        setButtonActive({ ...buttonActive, check_customer_alerts: true });
        const get = new fetch(`/user/vca/search/customer?account_id=${account_id}&type=phone_number&q=${phone_number}`).get();

        get.then((res) => {
            if (res.status === "error" || res === false) {
                setShowErrorFlash(true);
                setTimeout(() => {
                    setShowErrorFlash(false);
                }, 500);
            }
            if (res.status === "success") {
                setCustomerAlerts(res.data);
                if (["ringing", "inbound"].includes(status)) {
                    inboundCallRef.current = { ...inboundCallRef.current, caller_search: res.data || [], caller_name: (res.data || []).length > 0 ? (res.data[0].first_name || "") + " " + (res.data[0].last_name || "") : "" };
                }
                if (["search", "outbound"].includes(status)) {
                    setOutboundCall({ ...outboundCall, caller_search: res.data || [], caller_name: (res.data || []).length > 0 ? (res.data[0].first_name || "") + " " + (res.data[0].last_name || "") : "" });
                    outboundCallRef.current = { ...outboundCall, caller_search: res.data || [], caller_name: (res.data || []).length > 0 ? (res.data[0].first_name || "") + " " + (res.data[0].last_name || "") : "" };
                }
            }
            setButtonActive({ ...buttonActive, check_customer_alerts: false });
        }).catch((e) => {
            setButtonActive({ ...buttonActive, check_customer_alerts: false });
        });
    };

    const force_end_call = async (sid: string) => {
        const post = new fetch(`/user/vca/twilio/call-action`, { sid: sid, action: "force end call", conference_sid: outboundCall.conference_sid }).post();

        post.then((res) => {
            if (res.status === "error" || res === false) return force_end_call(sid);
        });
        post.catch(() => {
            return force_end_call(sid);
        });
    };

    const call_status = async (action: string, state_data: any = {}) => {
        const get = new fetch(`/user/vca/twilio/call-status?conference_sid=${status === "inbound" ? inboundCallRef.current.conference_sid : outboundCall.conference_sid}`).get();

        get.then(async (res) => {
            if (res.status === "error" || res === false) {
                return await call_status(action);
            }
            if (res.status === "success") {
                if (status === "inbound") {
                    inboundCallRef.current = {
                        ...inboundCallRef.current,
                        last_call_attempt: action === "make call v2" ? state_data?.last_call_attempt : inboundCallRef.current?.last_call_attempt || "",
                        participant_agent_call_sid: res.data.participant_agent_call_sid,
                        participant_agent_hold: res.data.participant_agent_hold,
                        participant_agent_muted: res.data.participant_agent_muted,
                        participant_agent_status: res.data.participant_agent_status,
                        participant_caller_call_sid: res.data.participant_caller_call_sid,
                        participant_caller_hold: res.data.participant_caller_hold,
                        participant_caller_muted: res.data.participant_caller_muted,
                        participant_caller_status: res.data.participant_caller_status,
                        participant_client_call_sid: res.data.participant_client_call_sid,
                        participant_client_hold: res.data.participant_client_hold,
                        participant_client_muted: res.data.participant_client_muted,
                        participant_client_status: res.data.participant_client_status,
                    };
                }
                if (status === "outbound") {
                    setOutboundCall({
                        ...outboundCall,
                        last_call_attempt: action === "make call outbound" ? state_data?.last_call_attempt : outboundCall?.last_call_attempt || "",
                        participant_agent_call_sid: res.data.participant_agent_call_sid,
                        participant_agent_hold: res.data.participant_agent_hold,
                        participant_agent_muted: res.data.participant_agent_muted,
                        participant_agent_status: res.data.participant_agent_status,
                        participant_caller_call_sid: res.data.participant_caller_call_sid,
                        participant_caller_hold: res.data.participant_caller_hold,
                        participant_caller_muted: res.data.participant_caller_muted,
                        participant_caller_status: res.data.participant_caller_status,
                        participant_client_call_sid: res.data.participant_client_call_sid,
                        participant_client_hold: res.data.participant_client_hold,
                        participant_client_muted: res.data.participant_client_muted,
                        participant_client_status: res.data.participant_client_status,
                    });

                    outboundCallRef.current = {
                        ...outboundCall,
                        last_call_attempt: action === "make call outbound" ? state_data?.last_call_attempt : outboundCall?.last_call_attempt || "",
                        participant_agent_call_sid: res.data.participant_agent_call_sid,
                        participant_agent_hold: res.data.participant_agent_hold,
                        participant_agent_muted: res.data.participant_agent_muted,
                        participant_agent_status: res.data.participant_agent_status,
                        participant_caller_call_sid: res.data.participant_caller_call_sid,
                        participant_caller_hold: res.data.participant_caller_hold,
                        participant_caller_muted: res.data.participant_caller_muted,
                        participant_caller_status: res.data.participant_caller_status,
                        participant_client_call_sid: res.data.participant_client_call_sid,
                        participant_client_hold: res.data.participant_client_hold,
                        participant_client_muted: res.data.participant_client_muted,
                        participant_client_status: res.data.participant_client_status,
                    };
                }

                if (["make call v2", "make call outbound"].includes(action)) {
                    if (![0, 1].includes(Number(res.data.participant_client_status))) {
                        setTimeout(async () => {
                            await call_status(action, state_data);
                        }, 500);
                    }
                    if ([0, 1].includes(Number(res.data.participant_client_status))) {
                        setTimerClientCallDuration({ ...timerClientCallDuration, ts: Number(res.data.participant_client_status) === 0 ? 0 : new Date().getTime() });
                    }
                }
            }
        }).catch(async (e) => {
            await call_status(action, state_data);
        });
    };

    const call_action = async (action: string, data: any = {}) => {
        const actionKey = ["make call v2", "make call outbound"].includes(action) ? action + data.btn_index : action;

        // Check the ref immediately - this prevents duplicate clicks
        if (buttonActiveRef.current.call_action[actionKey] === true) return;

        try {
            // Update both ref AND state
            buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [actionKey]: true } };
            setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [actionKey]: true } }));

            const post = new fetch(`/user/vca/twilio/call-action`, {
                ...data,
                action: action,
                conference_sid: inboundCallRef.current.conference_sid || outboundCall.conference_sid_uuid || outboundCall.conference_sid,
                caller_call_sid: inboundCallRef.current.participant_caller_call_sid || outboundCallRef.current.participant_caller_call_sid || "",
                outbound_agent_call_sid: outboundCallRef.current.participant_agent_call_sid || "",
            }).post();

            post.then((res) => {
                if (res.status === "error" || res === false) {
                    setShowErrorFlash(true);
                    setTimeout(() => {
                        setShowErrorFlash(false);
                    }, 500);
                }
                if (res.status === "success") {
                    agent_call_status();
                    if (action !== "wrapping up done") {
                        call_status(action, ["make call v2", "make call outbound"].includes(action) ? { last_call_attempt: data?.phone_number || "" } : {});
                        buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [action]: false } };
                        setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [action]: false } }));
                    }
                    if (action === "merge call v2") {
                        if (statusRef.current === "inbound" || status === "inbound") {
                            setStatus("wrapping up");
                            statusRef.current = "wrapping up";
                            setTimerWrapping({ ...timerWrapping, ts: new Date().getTime() });
                            setTimerCallDuration({ ts: 0, string: "00:00" });
                            setTimerAgentCallDuration({ ts: 0, string: "00:00" });
                            setTimerClientCallDuration({ ts: 0, string: "00:00" });
                            setShowKeyPad(false);
                        }
                    }
                }
                if (["wrapping up done", "no caller"].includes(action)) {
                    buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [action]: false } };
                    setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [action]: false } }));
                    inboundCallRef.current = {};
                    outboundCallRef.current = {};
                    setActiveClientId(0);
                    // console.log("bbbbb");
                    setActiveClient({});
                    setSendMessage({});
                    setConditionalFormAnswers([]);
                    setCustomerAlerts([]);
                    setStatus("");
                    statusRef.current = "";
                    setMessageSent({});
                    activeClientIdRef.current = 0;
                    activeClientRef.current = {};
                } else {
                    buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [action]: false } };
                    setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [action]: false } }));
                }
            })
                .catch((e) => {
                    buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [actionKey]: false } };
                    setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [actionKey]: false } }));
                    if (action === "make call v2") {
                        inboundCallRef.current = { ...inboundCallRef.current, last_call_attempt: data?.phone_number || "" };
                    }
                })
                .finally(() => {
                    setTimeout(() => {
                        buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [actionKey]: false } };
                        setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [actionKey]: false } }));
                    }, 500);
                });
        } catch (e) {
            // console.log(e);
        }
    };

    const stop_audio = (a: any) => {
        a.current.pause();
        a.current.currentTime = 0;
    };

    const agent_call_status = async (fresh = false) => {
        const get = new fetch(`/user/vca/twilio/agent-call-status?conference_sid=${inboundCallRef.current.conference_sid || outboundCall.conference_sid_uuid || outboundCall.conference_sid || (fresh === true ? "fresh" : "")}`).get();

        get.then(async (res) => {
            if (res.status === "error" || res === false) {
                return await agent_call_status();
            }
            if (res.status === "success") {
                if (["in-progress", "wrapping-up", ""].includes(res.data.status)) {
                    setStatus(res.data.status === "wrapping-up" ? "wrapping up" : res.data.status === "in-progress" ? statusRef.current : "");
                    statusRef.current = res.data.status === "wrapping-up" ? "wrapping up" : res.data.status === "in-progress" ? statusRef.current : "";
                    if (
                        JSON.stringify(sendMessage) === "{}" &&
                        res.data.status === "wrapping-up" &&
                        ((inboundCallRef.current || outboundCall)?.participant_agent_status === 0 || JSON.stringify(inboundCallRef.current || outboundCall) === "{}" || inboundCallRef.current?.data === null) &&
                        activeClientIdRef.current === 0
                    ) {
                        outerLoop: for (let i = 0; i < (clients || []).length; i++) {
                            for (let index = 0; index < clients[i].dids.length; index++) {
                                if (clients[i].dids[index].did === res.data.did) {
                                    setActiveClientId(Number(clients[i].id));
                                    // console.log("ccccc");
                                    setActiveClient(clients[i]);
                                    activeClientIdRef.current = Number(clients[i].id);
                                    activeClientRef.current = clients[i];
                                    // check_customer_alerts(inboundCallRef.current.from, Number(clients[i].id));
                                    if (status === "inbound") {
                                        inboundCallRef.current = { ...inboundCallRef.current, did: res.data.did, conference_sid: res.data.conference_sid };
                                    } else {
                                        // Might fix message miss match errors
                                        // setOutboundCall({ ...outboundCall, did: res.data.did, conference_sid: res.data.conference_sid });
                                        // outboundCallRef.current = {
                                        //     ...outboundCall,
                                        //     did: res.data.did,
                                        //     conference_sid: res.data.conference_sid,
                                        // };
                                    }
                                    break outerLoop;
                                }
                            }
                        }
                    }
                    if (res.data.status === "wrapping-up") {
                        setTimerWrapping({ ...timerWrapping, ts: Number(res.data.ts) });
                    }
                }
            }
        }).catch(async (e) => {
            await agent_call_status();
        });
    };

    const make_outbound = async (isBillable: string, start_call: any = {}, is_bhvm: boolean = false) => {
        if (["+611300", "+611800", "+6113"].some((p) => (start_call?.did || outboundCall.did).replace(/ /g, "").trim().startsWith(p))) return alert("You cannot make a call from this number.");
        setButtonActive({ ...buttonActive, outbound: true });
        const uuid = await new fetch(`/user/vca/create/uuid`).get();
        if (uuid.status === "success") {
            if ((uuid.data.uuid || "").length === 36) {
                setStatus("outbound");
                statusRef.current = "outbound";
                const a = await twilioDevice.connect({
                    params: {
                        To: ((start_call?.caller_prefix || outboundCall.caller_prefix) + (start_call?.destination || outboundCall.destination)).replace(/ /g, "").trim(),
                        From: (start_call?.did || outboundCall.did).replace(/ /g, "").trim(),
                        isBillable: isBillable,
                        isBhvm: is_bhvm ? "true" : "false",
                        isOutbound: "true",
                        newConferenceId: uuid.data.uuid,
                    },
                });

                setOutboundCall({
                    ...outboundCall,
                    did: (start_call?.did || outboundCall.did).replace(/ /g, "").trim(),
                    conference_sid: uuid.data.uuid,
                    conference_sid_uuid: uuid.data.uuid,
                    from: (start_call?.did || outboundCall.did).replace(/ /g, "").trim(),
                    to: ((start_call?.caller_prefix || outboundCall.caller_prefix) + (start_call?.destination || outboundCall.destination)).replace(/ /g, "").trim(),
                    twilio_active_call: a,
                });

                outboundCallRef.current = {
                    ...outboundCall,
                    did: (start_call?.did || outboundCall.did).replace(/ /g, "").trim(),
                    conference_sid: uuid.data.uuid,
                    conference_sid_uuid: uuid.data.uuid,
                    from: (start_call?.did || outboundCall.did).replace(/ /g, "").trim(),
                    to: ((start_call?.caller_prefix || outboundCall.caller_prefix) + (start_call?.destination || outboundCall.destination)).replace(/ /g, "").trim(),
                    twilio_active_call: a,
                };

                setTimerAgentCallDuration({ ts: new Date().getTime() });
                setTimerCallDuration({ ts: new Date().getTime() });

                a.on("cancel", () => {
                    // console.log("CANCELLLED");
                    setStatus("");
                    statusRef.current = "";
                    // NEWLY ADDED zzz
                    inboundCallRef.current = {};
                    outboundCallRef.current = {};
                    setActiveClientId(0);
                    setActiveClient({});
                    setSendMessage({});
                    setConditionalFormAnswers([]);
                    setCustomerAlerts([]);
                    setMessageSent({});
                    activeClientIdRef.current = 0;
                    activeClientRef.current = {};
                });

                a.on("disconnect", () => {
                    // console.log("DISCONNECCTEEEDD");
                    if (statusRef.current === "outbound") {
                        setStatus("wrapping up");
                        statusRef.current = "wrapping up";
                        setTimerWrapping({ ...timerWrapping, ts: new Date().getTime() });
                        setTimerCallDuration({ ts: 0, string: "00:00" });
                        setTimerAgentCallDuration({ ts: 0, string: "00:00" });
                        setTimerClientCallDuration({ ts: 0, string: "00:00" });
                        setShowKeyPad(false);
                    }
                });
            }
        } else {
            if (uuid.status === "error" || uuid === false) {
                setShowErrorFlash(true);
                setTimeout(() => {
                    setShowErrorFlash(false);
                }, 500);
            }
        }
        setButtonActive({ ...buttonActive, outbound: false });
    };

    const answer_call_v2 = async () => {
        setButtonActive({ ...buttonActive, outbound: true, send_message: false, ["wrapping up done"]: false });
        stop_audio(ringing);
        setEndCallSeconds(0);

        setStatus("inbound");
        statusRef.current = "inbound";

        const a = await twilioDevice.connect({
            params: {
                // To: `+61255007799`,
                From: `agent:agent_id_${user.user_id}`,
                caller_sid: inboundCallRef.current.call_sid,
                // caller_sid: socketCallQueue[0].sid,
                agent_id: user.user_id,
                vca_version: 2,
            },
        });

        // agent_call_status();

        inboundCallRef.current = { ...inboundCallRef.current, twilio_active_call: a };

        setTimerAgentCallDuration({ ts: new Date().getTime() });
        setTimerCallDuration({ ts: new Date().getTime() });

        const _has_call: any = async (participant_caller_call_sid = "") => {
            const get = await new fetch(`/user/vca/twilio/call-status?version=2&participant_caller_call_sid=${participant_caller_call_sid}`).get();

            if (get?.status !== "success") return _has_call(participant_caller_call_sid);
            if (get?.status === "success") {
                if (get?.data?.participant_agent_call_sid === "") return _has_call(participant_caller_call_sid);
                if (a.parameters.CallSid !== get?.data?.participant_agent_call_sid) {
                    inboundCallRef.current = {};
                    outboundCallRef.current = {};
                    setTimerCallDuration({ ts: 0, string: "00:00" });
                    setTimerAgentCallDuration({ ts: 0, string: "00:00" });
                    setTimerClientCallDuration({ ts: 0, string: "00:00" });
                    setShowKeyPad(false);
                    setActiveClientId(0);
                    // console.log("ddddd");
                    setActiveClient({});
                    activeClientIdRef.current = 0;
                    activeClientRef.current = {};
                    setSendMessage({});
                    setConditionalFormAnswers([]);
                    setCustomerAlerts([]);
                    setStatus("");
                    statusRef.current = "";
                    setMessageSent({});
                }
            }
        };

        // _has_call(a.customParameters.get("caller_sid"));

        // setOutboundCall({
        //     ...outboundCall,
        //     did: outboundCall.did,
        //     conference_sid: uuid.data.uuid,
        //     conference_sid_uuid: uuid.data.uuid,
        //     from: outboundCall.did,
        //     to: outboundCall.caller_prefix + outboundCall.destination,
        //     twilio_active_call: a,
        // });
        // setTimerAgentCallDuration({ ts: new Date().getTime() });
        // setTimerCallDuration({ ts: new Date().getTime() });

        a.on("cancel", () => {
            inboundCallRef.current = {};
            outboundCallRef.current = {};
            setTimerCallDuration({ ts: 0, string: "00:00" });
            setTimerAgentCallDuration({ ts: 0, string: "00:00" });
            setTimerClientCallDuration({ ts: 0, string: "00:00" });
            setShowKeyPad(false);
            setActiveClientId(0);
            // console.log("eeeee");
            setActiveClient({});
            setSendMessage({});
            setConditionalFormAnswers([]);
            setCustomerAlerts([]);
            setStatus("");
            statusRef.current = "";
            setMessageSent({});
            activeClientIdRef.current = 0;
            activeClientRef.current = {};
            agent_call_status();
        });

        a.on("disconnect", () => {
            // console.log("DISSSS");
            if (statusRef.current === "inbound") {
                setStatus("wrapping up");
                statusRef.current = "wrapping up";
                setTimerWrapping({ ...timerWrapping, ts: new Date().getTime() });
                setTimerCallDuration({ ts: 0, string: "00:00" });
                setTimerAgentCallDuration({ ts: 0, string: "00:00" });
                setTimerClientCallDuration({ ts: 0, string: "00:00" });
                setShowKeyPad(false);
                // agent_call_status();
            }
            //  ON END  AFTER A FEW CALLS DOESNT GO TO WRAPPING. STATUS ISSUE?
        });
        // }

        setButtonActive({ ...buttonActive, outbound: false });
    };

    const completed_caller_alert = async (data: any) => {
        //

        try {
            const post = new fetch(`/user/vca/update/alerts`, {
                action: "caller",
                customer_uuid: data.customer_uuid,
                alert_uuid: data.alert_uuid,
                account_id: data.account_id,
            }).post();

            post.then((res) => {
                if (res.status === "error" || res === false) {
                    setShowErrorFlash(true);
                    setTimeout(() => {
                        setShowErrorFlash(false);
                    }, 500);
                }
                if (res.status === "success") {
                    check_customer_alerts(outboundCall.caller_prefix + outboundCall.destination);
                }
            }).catch((e) => {});
        } catch (e) {
            // console.log(e);
        } finally {
        }
    };

    const handleSendDigit = (digit: string) => {
        let activeCall = null;

        if (status === "inbound" && inboundCallRef.current?.twilio_active_call) {
            activeCall = inboundCallRef.current.twilio_active_call;
        } else if (status === "outbound" && outboundCallRef.current?.twilio_active_call) {
            activeCall = outboundCallRef.current.twilio_active_call;
        }

        if (!activeCall) {
            console.error("No active call found");
            return;
        }

        if (typeof activeCall.sendDigits !== "function") {
            console.error("sendDigits not available");
            return;
        }

        try {
            activeCall.sendDigits(digit);
            // console.log(`Sent digit: ${digit}`);
        } catch (error) {
            console.error("Error sending digit:", error);
        }
    };

    useEffect(() => {
        // console.log(customerAlerts, ">>");
        const prev_status = statusRef.current;
        statusRef.current = status;
        if (statusRef.current === "") {
            setTimerCallDuration({ ts: 0, string: "00:00" });
            setTimerAgentCallDuration({ ts: 0, string: "00:00" });
            setTimerClientCallDuration({ ts: 0, string: "00:00" });
            setShowKeyPad(false);
            setActiveClientId(0);
            setActiveClient({});
            activeClientIdRef.current = 0;
            activeClient.current = {};
            setSendMessage({});
            setConditionalFormAnswers([]);
            setCustomerAlerts([]);
            setMessageSent({});
        }
        if (!["", "ringing"].includes(statusRef.current)) stop_audio(ringing);
        if (statusRef.current === "ringing") {
            if (JSON.stringify(inboundCallRef.current) !== "{}" && activeClientId === 0) {
                outerLoop: for (let i = 0; i < (clients || []).length; i++) {
                    for (let index = 0; index < (clients[i].dids || []).length; index++) {
                        if (clients[i].dids[index].did === inboundCallRef.current.did) {
                            setActiveClientId(Number(clients[i].id));
                            setActiveClient(clients[i]);
                            activeClientIdRef.current = Number(clients[i].id);
                            activeClientRef.current = clients[i];
                            check_customer_alerts(inboundCallRef.current.from, Number(clients[i].id));
                            break outerLoop;
                        }
                    }
                }
            }
        }
        if (prev_status === "ringing" && statusRef.current === "" && statusRef.current !== "wrapping up" && prev_status !== "wrapping up") {
            // call was canceled by caller
            setTimerCallDuration({ ts: 0, string: "00:00" });
            setTimerAgentCallDuration({ ts: 0, string: "00:00" });
            setTimerClientCallDuration({ ts: 0, string: "00:00" });
            setShowKeyPad(false);
            setActiveClientId(0);
            // console.log("aaaa");
            setActiveClient({});
            activeClientIdRef.current = 0;
            activeClient.current = {};
            setSendMessage({});
            setConditionalFormAnswers([]);
            setCustomerAlerts([]);
            setMessageSent({});
        }
    }, [status, inboundCallRef.current]);

    useEffect(() => {
        const _is_true = (obj: any): boolean => Object.values(obj).some((val) => (typeof val === "object" ? _is_true(val) : val === true));
        if (_is_true(buttonActive) === false) {
            if (status === "outbound") {
                setOutboundCall({
                    ...outboundCall,
                    ...socketCallStatus,
                });
                outboundCallRef.current = {
                    ...outboundCall,
                    ...socketCallStatus,
                };
            } else {
                inboundCallRef.current = {
                    ...inboundCallRef.current,
                    ...socketCallStatus,
                };
            }
        }
        if (status === "inbound") {
            if (Number(socketCallStatus.participant_agent_status) === 1 && Number(socketCallStatus.participant_caller_status) === 0 && Number(socketCallStatus.participant_client_status) === 0) {
                inboundCallRef.current.twilio_active_call.disconnect();
            }
        }
        if (![1, 2].includes(Number(socketCallStatus.participant_client_status))) {
            setActiveTransfer(-1);
        }
        // if ([1, 2].includes(Number(socketCallStatus.participant_client_status))) {
        //     beep_sound.current.play();
        // }
        if (Number(socketCallStatus.participant_agent_status) === 0 && socketCallStatus.status === "wrapping-up") {
            setStatus("wrapping up");
            statusRef.current = "wrapping up";
        }
    }, [socketCallStatus]);

    useEffect(() => {
        const interval = setInterval(() => {
            const a = (socketCallQueue || []).length === 0 ? "00:00" : moment.utc(new Date().getTime() - Math.min(...(socketCallQueue || []).map((item: any) => item.caller_called_ts))).format("mm:ss");
            setTimerLongestInQueue(a);

            if (!["inbound", "outbound"].includes(statusRef.current)) {
                if (["", "ringing"].includes(statusRef.current)) {
                    // remove answer screen if call no inbound and no longer active

                    if (statusRef.current === "ringing") {
                        let sids = [];
                        // for (let i = 0; i < socketCallQueue.length; i++) sids.push(socketCallQueue[i].conference_sid);

                        // if (!sids.includes(inboundCallRef.current.conference_sid)) {
                        //     setShowKeyPad(false);
                        //     setActiveClientId(0);
                        //     setActiveClient({});
                        //     setSendMessage({});
                        //     // setConditionalFormAnswers([]);
                        //     setCustomerAlerts([]);
                        //     setStatus("");
                        //     statusRef.current = "";
                        //     setMessageSent({});
                        //     activeClientIdRef.current = 0;
                        //     activeClientRef.current = {};
                        //     agent_call_status();
                        // }
                    }

                    if ((socketCallQueue || []).length > 0) {
                        let skip = true;

                        for (let index = 0; index < (user.answer_group || []).length; index++) {
                            for (let index2 = 0; index2 < (socketCallQueue[0].answer_group || []).length; index2++) {
                                if ((user.answer_group || [])[index] === (socketCallQueue[0].answer_group || [])[index2]) {
                                    skip = false;
                                }
                            }
                        }

                        // Only ring and clear ringing status if status available or ringing
                        if (statusRef.current === "wrapping up" && socketCallQueue.length > 0 && skip === false) {
                            ringing.current.play();
                            // if no call in queue dont ring
                        }

                        if (skip === true) return;
                        ringing.current.play();

                        inboundCallRef.current = {
                            ...inboundCallRef.current,
                            did: socketCallQueue[0].did,
                            conference_sid: socketCallQueue[0].conference_sid,
                            call_sid: socketCallQueue[0].sid,
                            from: socketCallQueue[0].caller,
                            to: socketCallQueue[0].did,
                        };
                        outerLoop: for (let i = 0; i < (clients || []).length; i++) {
                            for (let index = 0; index < (clients[i].dids || []).length; index++) {
                                if (clients[i].dids[index].did === socketCallQueue[0].did) {
                                    setActiveClientId(Number(clients[i].id));
                                    setActiveClient(clients[i]);
                                    activeClientIdRef.current = Number(clients[i].id);
                                    activeClientRef.current = clients[i];
                                    check_customer_alerts(socketCallQueue[0].caller, Number(clients[i].id));
                                    break outerLoop;
                                }
                            }
                        }
                        // console.log("zzzz");
                        if (statusRef.current !== "wrapping up") {
                            setStatus("ringing");
                            statusRef.current = "ringing";
                        }
                    } else if (statusRef.current !== "wrapping up") {
                        setStatus("");
                        statusRef.current = "";
                        stop_audio(ringing);
                    }
                }
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [socketCallQueue]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (status === "wrapping up") {
                const a = timerWrapping.ts === 0 ? "00:00" : moment.utc(new Date().getTime() - timerWrapping.ts).format("mm:ss");
                setTimerWrapping({ ts: timerWrapping.ts, string: a });
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [timerWrapping.ts]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (["inbound", "outbound"].includes(status)) {
                const a = timerAgentCallDuration.ts === 0 ? "00:00" : moment.utc(new Date().getTime() - timerAgentCallDuration.ts).format("mm:ss");
                if (status === "inbound") {
                    const _total = Number(new Date().getTime() - timerAgentCallDuration.ts) / 1000;
                    let max_call_duration = { warning: 0, end: 0 };
                    if (user.max_call_duration?.end) {
                        max_call_duration = user.max_call_duration;
                        if (Number(max_call_duration?.warning) !== 0 && _total >= Number(max_call_duration?.warning) && _total <= Number(max_call_duration?.warning) + 1) {
                            warning_sound.current.play();
                            setShowWarningFlash(true);
                            setTimeout(() => {
                                setShowWarningFlash(false);
                            }, 500);
                        }
                        if (Number(max_call_duration?.end) !== 0 && _total >= Number(max_call_duration?.end) && _total <= Number(max_call_duration?.end) + 1) {
                            end_sound.current.play();
                            setShowErrorFlash(true);
                            setTimeout(() => {
                                setShowErrorFlash(false);
                            }, 500);
                        }
                    } else {
                        max_call_duration = (activeClient?.dids || "").length > 0 ? activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === inboundCallRef.current.did)]?.max_call_duration : { warning: 0, end: 0 };
                        if (Number(max_call_duration?.warning || 0) !== 0 && _total >= Number(max_call_duration?.warning || 0) && _total <= Number(max_call_duration?.warning || 0) + 1) {
                            warning_sound.current.play();
                            setShowWarningFlash(true);
                            setTimeout(() => {
                                setShowWarningFlash(false);
                            }, 500);
                        }
                        if (Number(max_call_duration?.end || 0) !== 0 && _total >= Number(max_call_duration?.end || 0) && _total <= Number(max_call_duration?.end || 0) + 1) {
                            end_sound.current.play();
                            setShowErrorFlash(true);
                            setTimeout(() => {
                                setShowErrorFlash(false);
                            }, 500);
                        }
                    }
                }
                setTimerAgentCallDuration({ ts: timerAgentCallDuration.ts, string: a });
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [timerAgentCallDuration.ts]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (["inbound", "outbound"].includes(status)) {
                const a = timerClientCallDuration.ts === 0 ? "00:00" : moment.utc(new Date().getTime() - timerClientCallDuration.ts).format("mm:ss");
                setTimerClientCallDuration({ ts: timerClientCallDuration.ts, string: a });
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [timerClientCallDuration.ts]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (["inbound", "outbound"].includes(status)) {
                const a = timerCallDuration.ts === 0 ? "00:00" : moment.utc(new Date().getTime() - timerCallDuration.ts).format("mm:ss");
                setTimerCallDuration({ ...timerCallDuration, ts: timerCallDuration.ts, string: a });
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [timerCallDuration.ts]);

    useEffect(() => {
        if (JSON.stringify(activeClientRef.current) !== JSON.stringify(activeClient)) activeClientRef.current = activeClient;
        if (JSON.stringify(activeClientIdRef.current) !== JSON.stringify(activeClientId)) activeClientIdRef.current = activeClientId;
    }, [activeClient, activeClientId, activeClientRef, activeClientIdRef]);

    useEffect(() => {
        activeClientIdRef.current = activeClientId;
        if (JSON.stringify(modalData) === "{}") {
            if (!["ringing", "wrapping up"].includes(status)) {
                setStatus(activeClientId !== 0 ? "search" : "");
                statusRef.current = activeClientId !== 0 ? "search" : "";
                if (status === "search" && activeClientId !== 0 && JSON.stringify(activeClient) === "{}") {
                    send_message_state_prep();
                }
            } else if (["ringing"].includes(status)) {
                send_message_state_prep();
            }
        } else {
            send_message_state_prep();
        }
    }, [activeClientId, modalData]);

    useEffect(() => {
        if (!user.device_token) return;

        const device: any = new Device(user.device_token, { disableAudioContextSounds: false });

        device.register();
        device.audio.incoming(false);

        setTwilioDevice(device);

        device.on("registered", (call: any) => {
            // console.log(device.state);
        });

        device.on("error", (e: any) => {
            // console.log(e);
        });
    }, [user.device_token]);

    useEffect(() => {
        const handle = (e: any) => {
            if (e.metaKey && e.key === "j" && !["inbound", "wrapping up"].includes(status)) {
                setModalData({ toggle: true, type: "vca search client" });
            }
        };
        window.addEventListener("keydown", handle);
        return () => window.removeEventListener("keydown", handle);
    }, []);

    useEffect(() => {
        agent_call_status(true);
        LogRocket.identify(user.user_id, {
            name: user.first_name,
        });
    }, []);

    return (
        <>
            <div id="vca">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xxl-10 col-lg-9 pl-0">
                            <div id="vca-left">
                                <div id="vca-header">
                                    <div className="row">
                                        <div className="col-lg-12">
                                            <div className={Number(user.bhvm_tickets) === 1 && (bhvmTickets || []).length > 0 ? "container-fluid vca-emergency-header" : "d-none"}>
                                                <div className="row">
                                                    <div className="col-6 text-left">
                                                        <div>
                                                            <span className="bhvm-count">{(bhvmTickets || []).length}</span> pending BHVM{(bhvmTickets || []).length > 1 ? "'s" : ""}
                                                        </div>
                                                    </div>
                                                    <div className="col-6 text-right">
                                                        <Button
                                                            onClick={() => {
                                                                setModalData({ toggle: true, type: "bhvm tickets", position: "full" });
                                                            }}
                                                            value={`Manage ticket${(bhvmTickets || []).length > 1 ? "'s" : ""}`}
                                                            hideContainer
                                                            small
                                                            className="ml-4"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* <div className="col-12" style={{ overflow: "hidden" }}>
                                            <div style={{ border: "1px solid red", overflow: "hidden", whiteSpace: "pre", width: "100%" }}>
                                                <small style={{ fontSize: "10px", overflow: "hidden" }}>
                                                    {statusRef?.current || "-"}
                                                    {" <> "}
                                                    {status || "-"}
                                                    {" <> "}
                                                    {socketCallStatus?.status || "-"}
                                                    {" <> "}
                                                    {activeClientId || "-"}
                                                    {" <> "}
                                                    {inboundCallRef?.current?.did || "-"}
                                                    {" <> "}
                                                    {Object.keys(sendMessage || {}).length || "-"}
                                                </small>
                                            </div>
                                        </div> */}
                                        <div className="col-lg-3">
                                            <div>
                                                <Button
                                                    value="Search"
                                                    small
                                                    disabled={["inbound", "wrapping up", "outbound"].includes(status)}
                                                    hideContainer
                                                    onClick={() => {
                                                        setModalData({ toggle: true, type: "vca search client" });
                                                    }}
                                                />{" "}
                                                <Button
                                                    value="Log out"
                                                    className="ml-4"
                                                    color="red"
                                                    small
                                                    disabled={["inbound", "wrapping up", "outbound"].includes(status)}
                                                    hideContainer
                                                    onClick={() => {
                                                        window.location.href = "/logout";
                                                    }}
                                                />{" "}
                                                {Number(user.user_id) === 1 ? (
                                                    <Button
                                                        value="Error"
                                                        className="ml-1"
                                                        color="red"
                                                        small
                                                        outline
                                                        hideContainer
                                                        onClick={() => {
                                                            setActiveClient({ ...activeClient, locations: "EEE" });
                                                        }}
                                                    />
                                                ) : (
                                                    <></>
                                                )}
                                                <Button
                                                    value="Clear spinning"
                                                    className="float-right"
                                                    color="red"
                                                    small
                                                    outline
                                                    hideContainer
                                                    onClick={() => {
                                                        const a = window.confirm("Are you sure you want to clear the spinning?");
                                                        if (a) {
                                                            setButtonActive({
                                                                save_alert: false,
                                                                send_message: false,
                                                                check_customer_alerts: false,
                                                                call_action: {},
                                                            });
                                                        }
                                                        return;
                                                    }}
                                                />
                                                {Number(activeClient?.admin_section?.status) === 1 && Number(user.admin_section) === 1 ? (
                                                    <Button
                                                        value="Admin"
                                                        className="ml-1"
                                                        color="orange"
                                                        small
                                                        outline
                                                        hideContainer
                                                        onClick={() => {
                                                            setToggleAdminSection(!toggleAdminSection);
                                                        }}
                                                    />
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <div>
                                                <div className="small d-inline-block">Status</div>
                                                <div className="fw-600 d-inline-block ml-1">
                                                    {[""].includes(status)
                                                        ? "Available"
                                                        : ["search"].includes(status)
                                                          ? "Busy"
                                                          : ["outbound"].includes(status)
                                                            ? `Outbound call ${timerCallDuration.string}`
                                                            : ["ringing"].includes(status)
                                                              ? "Ringing"
                                                              : ["inbound"].includes(status)
                                                                ? `On call ${timerCallDuration.string}`
                                                                : ["wrapping up"].includes(status)
                                                                  ? `Wrapping up ${timerWrapping.string}`
                                                                  : ""}
                                                    {status === "inbound" ? (
                                                        user.max_call_duration?.end ? (
                                                            <div className="small red">Call will end after {user.max_call_duration?.end || ""}</div>
                                                        ) : (activeClient?.dids || "").length > 0 ? (
                                                            JSON.stringify(activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === (inboundCallRef.current.did || outboundCall.did))]?.max_call_duration || {}) !== "{}" ? (
                                                                <div className="small red">
                                                                    Call will end after{" "}
                                                                    {new Misc().to_duration(
                                                                        activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === (inboundCallRef.current.did || outboundCall.did))].max_call_duration?.end || "",
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                ""
                                                            )
                                                        ) : (
                                                            ""
                                                        )
                                                    ) : (
                                                        ""
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <div>
                                                <div className="small d-inline-block">Queue</div>

                                                <div className="fw-600 d-inline-block ml-1">
                                                    {(socketCallQueue.filter((item: any) => (user.answer_group || []).some((userAnswer: any) => (item.answer_group || []).includes(userAnswer))) || []).length} in queue
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <div>
                                                <div className="small d-inline-block">Longest in queue</div>
                                                <div className="fw-600 d-inline-block ml-1">
                                                    {(socketCallQueue.filter((item: any) => (user.answer_group || []).some((userAnswer: any) => (item.answer_group || []).includes(userAnswer))) || []).length > 0
                                                        ? timerLongestInQueue
                                                        : "00:00"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={activeClientIdRef.current === 0 ? "d-none" : ""} id="vca-header-2"></div>
                                <div className={activeClientIdRef.current === 0 ? "d-none" : ""} id="vca-header-3">
                                    <div className="col-12">
                                        <div className="row">
                                            <div className="col-lg-6">
                                                <div>
                                                    <div className="small red">Client Alerts</div>
                                                    <div dangerouslySetInnerHTML={{ __html: (activeClient.alerts || []).length > 0 ? activeClient?.alerts[0].alert : "" }}></div>
                                                </div>
                                            </div>
                                            <div className="col-lg-6" style={{ backgroundColor: "#FFF" }}>
                                                <div>
                                                    <div className="small red">Caller Alerts</div>
                                                    {(customerAlerts || [])
                                                        .filter((a: any) => a.alert?.alert !== null && a.alert !== null && Number(a.status) === 1)
                                                        .map((a: any, i: number, filteredArray: any[]) => (
                                                            <div key={a.id || i}>
                                                                <div
                                                                    className="small mb-1"
                                                                    style={{
                                                                        borderBottom: i + 1 !== filteredArray.length ? "1px solid RGBA(0,0,0,0.2)" : "",
                                                                    }}>
                                                                    <table>
                                                                        <tbody>
                                                                            <tr>
                                                                                <td className="vertical-align-top">
                                                                                    <b>Name</b> {i + 1} - {(customerAlerts || []).length}
                                                                                </td>
                                                                                <td>
                                                                                    {a.first_name} {a.last_name}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="vertical-align-top">
                                                                                    <b>Alert</b>
                                                                                </td>
                                                                                <td dangerouslySetInnerHTML={{ __html: he.decode(a.alert.alert || "") }}></td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>
                                                                                    <Button
                                                                                        value="Completed"
                                                                                        small
                                                                                        onClick={() => {
                                                                                            completed_caller_alert({ account_id: a.account_id, alert_uuid: a.alert.uuid, customer_uuid: a.uuid });
                                                                                        }}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={activeClientIdRef.current === 0 ? "d-none" : ""} id="vca-header-4">
                                    <div className="row">
                                        {/* <div className="col-lg-6">
                                            <div>
                                                <div className="small red">Caller Alerts</div>
                                                {(customerAlerts || []).map((a: any, i: number) => {
                                                    if (a.alert?.alert === null || a.alert === null || Number(a.status) !== 1) return <React.Fragment key={i} />;
                                                    return (
                                                        <div key={i}>
                                                            <div className="small mb-1">
                                                                <table>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="vertical-align-top">
                                                                                <b>Name</b>
                                                                            </td>
                                                                            <td>
                                                                                {a.first_name} {a.lsst_name}
                                                                            </td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="vertical-align-top">
                                                                                <b>Alert</b>
                                                                            </td>
                                                                            <td dangerouslySetInnerHTML={{ __html: he.decode(a.alert.alert || "") }}></td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                                <div className={activeClientIdRef.current === 0 ? "d-none" : ""} id="vca-left-body">
                                    <div className="row">
                                        <div className="col-lg-8">
                                            <div id="vca-left-header">
                                                <div className="blue fw-600">{he.decode(activeClient?.name || "")} - Answer Process</div>
                                                <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient.answer_process || "-") }}></div>
                                            </div>
                                            {toggleAdminSection ? (
                                                <div className="row">
                                                    <div id="vca-left-admin-left" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4", "#vca-left-header"])} className="col-lg-6">
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.admin_section?.left || "-") }}></div>
                                                    </div>
                                                    <div id="vca-left-admin-right" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4", "#vca-left-header"])} className="col-lg-6">
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.admin_section?.right || "-") }}></div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    <div id="vca-left-booking" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4", "#vca-left-header"])} className="col-lg-6">
                                                        {/* <div className="blue fw-600">Booking Rules</div> */}
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ padding: "5px 0" }}>
                                                            Logins
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.logins || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Cancellation Policy
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.cancellation_policy || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Same Day Appointment Policy
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.same_day_appt_policy || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Booking Rules
                                                            {/* booking rules = nothing_available */}
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.nothing_available || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Info to Get
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.info_to_get || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Appointment Info
                                                            {/* appointment info = appt_type */}
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.appt_type || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Hours
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.hours || "-") }}></div>
                                                    </div>
                                                    <div id="vca-left-info" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4", "#vca-left-header"])} className="col-lg-6">
                                                        <div className="orange fw-600 text-upper">Info to Give Out</div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.summary || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Services
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.services || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Pricing Info
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.pricing || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Locations
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.location || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            FAQs
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.faqs || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Other Info
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.other || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Address
                                                        </div>
                                                        <div>
                                                            <Button
                                                                small
                                                                value="Find closest location"
                                                                onClick={() => {
                                                                    setModalData({ toggle: true, type: "location search", data: activeClient });
                                                                }}
                                                            />
                                                            {(activeClient?.locations || []).map((a: any, i: number) => {
                                                                if (Number(a.status) !== 1) return <React.Fragment key={i} />;
                                                                return (
                                                                    <div key={i}>
                                                                        <div>{a.name}</div>
                                                                        <div>
                                                                            <u>Address</u>
                                                                        </div>
                                                                        <div className="mb-2">
                                                                            {a.address} {a.city} {a.state} {a.postcode}
                                                                        </div>
                                                                        <div>
                                                                            <u>Postal Address</u>
                                                                        </div>
                                                                        <div>
                                                                            {a.postal_address} {a.postal_city} {a.postal_state} {a.postal_postcode}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div id="vca-left-message" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4"])} className="col-lg-4">
                                            {/* <div className="row">
                                                <div className="col-3">
                                                    <Button className="small" color="green" value="New" />
                                                </div>
                                                <div className="col-3">
                                                    <Button className="small" color="green" value="New" />
                                                </div>
                                                <div className="col-3">
                                                    <Button className="small" color="green" value="New" />
                                                </div>
                                            </div> */}
                                            <div className="fw-600 mt-4">Message</div>

                                            <div className="row">
                                                {" "}
                                                <div className="col-12">
                                                    <div className="row">
                                                        <div className="col-6">
                                                            <div className="mt-4 fw-500">Caller Name</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <Input
                                                        container={false}
                                                        defaultValue={sendMessage.caller_first_name || ""}
                                                        changeDefault={new Date().getTime()}
                                                        name="messagefirstname"
                                                        type="text"
                                                        placeholder="First name"
                                                        className="input-small"
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, caller_first_name: v });
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-6">
                                                    <Input
                                                        container={false}
                                                        defaultValue={sendMessage.caller_last_name || ""}
                                                        changeDefault={new Date().getTime()}
                                                        name="messagelastname"
                                                        type="text"
                                                        placeholder="Last name"
                                                        className="input-small"
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, caller_last_name: v });
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-12">
                                                    <div className="mt-4 fw-500">Caller Phone Number</div>
                                                </div>
                                                <div className="col-12">
                                                    <Input
                                                        required
                                                        container={false}
                                                        defaultValue={sendMessage.caller_number || ""}
                                                        changeDefault={new Date().getTime()}
                                                        name="messagecallernumber"
                                                        type="text"
                                                        placeholder="Caller phone number"
                                                        className="input-small"
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, caller_number: v });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-4 fw-500">Send to</div>
                                            <Input
                                                required
                                                container={false}
                                                readOnly
                                                defaultValue={sendMessage.to_name || ""}
                                                changeDefault={new Date().getTime()}
                                                name="sendto"
                                                type="text"
                                                placeholder="Select from contacts list"
                                                className="input-small"
                                            />
                                            <div className="row">
                                                <div className={["wrapping up", "inbound", "outbound"].includes(status) ? "col-12 p-4" : "d-none"}>
                                                    <div className="search-caller">
                                                        <div className="row">
                                                            <div className="col-12">
                                                                <div className="fw-500">Search for Caller</div>
                                                            </div>
                                                            <div className="col-12">
                                                                <Select
                                                                    options={(status === "inbound" ? inboundCallRef.current?.caller_search || [] : outboundCall?.caller_search || []).map((a: any) => {
                                                                        return { label: a.first_name + " " + a.last_name, value: a.uuid };
                                                                    })}
                                                                    defaultValue={sendMessage?.customer_uuid || ""}
                                                                    name="customeruuid"
                                                                    placeholder="Select"
                                                                    className="input-small"
                                                                    container={false}
                                                                    updateChange={(v) => {
                                                                        let _caller_first_name = "",
                                                                            _caller_last_name = "",
                                                                            _caller_number = "";
                                                                        if (v) {
                                                                            const arr = status === "inbound" ? inboundCallRef.current?.caller_search : outboundCall?.caller_search;
                                                                            for (let index = 0; index < (arr || []).length; index++) {
                                                                                if (arr[index].uuid === v) {
                                                                                    _caller_first_name = arr[index].first_name;
                                                                                    _caller_last_name = arr[index].last_name;
                                                                                    _caller_number = arr[index].prefix + arr[index].phone_number;
                                                                                    break;
                                                                                }
                                                                            }
                                                                        }
                                                                        setSendMessage({
                                                                            ...sendMessage,
                                                                            customer_uuid: v,
                                                                            caller_first_name: _caller_first_name,
                                                                            caller_last_name: _caller_last_name,
                                                                            caller_number: _caller_number,
                                                                            searched_caller_number: _caller_number,
                                                                        });
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="col-12">
                                                                <Input
                                                                    // container={false}
                                                                    readOnly
                                                                    defaultValue={sendMessage.searched_caller_number || ""}
                                                                    changeDefault={new Date().getTime()}
                                                                    name="messagesearchcutsomernumber"
                                                                    type="text"
                                                                    placeholder="Caller phone number"
                                                                    className="input-small"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* {![1, 3].includes(Number(user.user_id)) ? (
                                                    <></>
                                                ) : ( */}
                                                <ConditionalForm
                                                    form={activeClient?.conditional_form}
                                                    update_answers={(v: any) => {
                                                        setConditionalFormAnswers(v);
                                                    }}
                                                    update_full_answers={(v: any) => {
                                                        setConditionalFormAnswersFull(v);
                                                    }}
                                                    update_current_question_contacts={(contacts: string[]) => {
                                                        setCurrentQuestionContacts(contacts);
                                                    }}
                                                    clear={JSON.stringify(conditionalFormAnswers) === "[]"}
                                                />
                                                {/* )} */}
                                                <div className="col-12">
                                                    <div className="mt-4 fw-500">Subject of Email</div>
                                                </div>
                                                <div className="col-12">
                                                    <Input
                                                        required
                                                        container={false}
                                                        defaultValue={sendMessage.subject || ""}
                                                        changeDefault={new Date().getTime()}
                                                        name="messagesubject"
                                                        type="text"
                                                        placeholder="Subject"
                                                        className="input-small"
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, subject: v });
                                                        }}
                                                    />
                                                </div>
                                                <div className={Number(activeClient.show_3rd_party || 0) === 1 ? "col-12" : "d-none"}>
                                                    <div className="mt-4 fw-500">Actions in Call</div>
                                                </div>
                                                <div className={Number(activeClient.show_3rd_party || 0) === 1 ? "col-12" : "d-none"}>
                                                    <Select
                                                        required
                                                        options={[
                                                            { label: "Yes", value: "1" },
                                                            { label: "No", value: "0" },
                                                        ].map((a: any) => {
                                                            return { label: a.label, value: a.value };
                                                        })}
                                                        defaultValue={sendMessage.third_party_action || ""}
                                                        name="message3pa"
                                                        placeholder="Select"
                                                        className="input-small"
                                                        container={false}
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, third_party_action: v });
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {((activeClient.forms || []).length > 0 ? activeClient.forms[0].form.form.form || [] : []).map((a: any, i: number) => {
                                                if (Number(activeClient?.conditional_form?.status || 0) === 1) return <React.Fragment key={i} />;

                                                if (a.type === "multiplechoice") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            {(a?.properties?.items || []).map((b: any, ii: number) => {
                                                                return (
                                                                    <div key={ii} className="mb-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`radio-${ii}`}
                                                                            value={b}
                                                                            checked={((sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || [] : []).includes(b)}
                                                                            onChange={() => {
                                                                                /*leave onchange to fix browser error*/
                                                                            }}
                                                                            onClick={(e: any) => {
                                                                                const v = e.target?.value,
                                                                                    c = e.target?.checked;

                                                                                let arr = sendMessage?.custom || [],
                                                                                    new_custom = [];
                                                                                for (let index = 0; index < (arr || []).length; index++) {
                                                                                    if (index === i) {
                                                                                        let _arr = arr[index].value || [];
                                                                                        if (c === true) {
                                                                                            if (!_arr.includes(_arr)) _arr.push(v);
                                                                                        } else {
                                                                                            _arr = _arr.filter((_item: any) => _item !== v);
                                                                                        }
                                                                                        new_custom.push({ ...arr[index], [he.decode(a?.label || "")]: { ...arr[index][he.decode(a?.label || "")], value: _arr } });
                                                                                    } else {
                                                                                        new_custom.push(arr[index]);
                                                                                    }
                                                                                }

                                                                                setSendMessage({ ...sendMessage, custom: new_custom });
                                                                            }}
                                                                        />{" "}
                                                                        <div className="d-inline-block">{b}</div>
                                                                    </div>
                                                                );
                                                            })}
                                                            {a.mandatory === true && ((sendMessage?.custom || "").length > 0 ? sendMessage?.custom[i]?.[he.decode(a?.label || "")]?.value || "" : "").length === 0 ? (
                                                                <div className="red">{he.decode(a?.label || "")} is required</div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "singlechoice") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            <Select
                                                                container={false}
                                                                options={(a?.properties?.items || []).map((a: any) => {
                                                                    return { label: a, value: a };
                                                                })}
                                                                name={`message${i}`}
                                                                placeholder={he.decode(a?.label || "")}
                                                                className="input-small"
                                                                defaultValue={(sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : ""}
                                                                updateChange={(v) => {
                                                                    let arr = sendMessage?.custom || [],
                                                                        new_custom = [];
                                                                    for (let index = 0; index < arr.length; index++) {
                                                                        if (index === i) {
                                                                            new_custom.push({ ...arr[index], [he.decode(a?.label || "")]: { ...arr[index][he.decode(a?.label || "")], value: v } });
                                                                        } else {
                                                                            new_custom.push(arr[index]);
                                                                        }
                                                                    }

                                                                    setSendMessage({ ...sendMessage, custom: new_custom });
                                                                }}
                                                            />
                                                            {a.mandatory === true && ((sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : "").length === 0 ? (
                                                                <div className="red">{he.decode(a?.label || "")} is required</div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "number") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            <Input
                                                                container={false}
                                                                type="number"
                                                                name={`message${i}`}
                                                                placeholder={he.decode(a?.label || "")}
                                                                className="input-small"
                                                                defaultValue={(sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : ""}
                                                                changeDefault={new Date().getTime()}
                                                                updateChange={(v) => {
                                                                    let arr = sendMessage?.custom || [],
                                                                        new_custom = [];
                                                                    for (let index = 0; index < arr.length; index++) {
                                                                        if (index === i) {
                                                                            new_custom.push({ ...arr[index], [he.decode(a?.label || "")]: { ...arr[index][he.decode(a?.label || "")], value: v } });
                                                                        } else {
                                                                            new_custom.push(arr[index]);
                                                                        }
                                                                    }

                                                                    setSendMessage({ ...sendMessage, custom: new_custom });
                                                                }}
                                                            />
                                                            {a.mandatory === true && ((sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : "").length === 0 ? (
                                                                <div className="red">{he.decode(a?.label || "")} is required</div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "text") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            <Textarea
                                                                container={false}
                                                                autoResize={true}
                                                                name={`message${i}`}
                                                                placeholder={he.decode(a?.label || "")}
                                                                defaultValue={(sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : ""}
                                                                changeDefault={new Date().getTime()}
                                                                updateChange={(v) => {
                                                                    let arr = sendMessage?.custom || [],
                                                                        new_custom = [];
                                                                    for (let index = 0; index < arr.length; index++) {
                                                                        if (index === i) {
                                                                            new_custom.push({ ...arr[index], [he.decode(a?.label || "")]: { ...arr[index][he.decode(a?.label || "")], value: v } });
                                                                        } else {
                                                                            new_custom.push(arr[index]);
                                                                        }
                                                                    }

                                                                    setSendMessage({ ...sendMessage, custom: new_custom });
                                                                }}
                                                            />
                                                            {a.mandatory === true && ((sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : "").length === 0 ? (
                                                                <div className="red">{he.decode(a?.label || "")} is required</div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "section") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-600">{a.properties.heading}</div>
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "instruction") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            <div className="">{a.properties.text}</div>
                                                        </div>
                                                    );
                                                }

                                                return <React.Fragment key={i} />;
                                            })}

                                            <div className="text-right">
                                                <Button
                                                    value="Submit"
                                                    active={buttonActive.send_message}
                                                    onClick={() => {
                                                        send_message();
                                                    }}
                                                    small
                                                />
                                            </div>
                                            {/*  */}
                                            <div
                                                className={
                                                    ["outbound", "search"].includes(statusRef.current) ||
                                                    (statusRef.current === "wrapping up" && (outboundCall.destination || "").length !== 0 && (outboundCall.caller_prefix || "").length !== 0)
                                                        ? " my-4"
                                                        : "d-none"
                                                }
                                                style={{ borderTop: "1px solid #ccc" }}>
                                                <div className="fw-600 mt-4">Create customer alert</div>
                                                <Textarea
                                                    autoResize={true}
                                                    name="createcustomeralert"
                                                    placeholder="Alert"
                                                    updateChange={(v: any) => {
                                                        setCreateCustomerAlert(v);
                                                    }}
                                                />
                                                <div className="text-right">
                                                    <Button
                                                        value="Save alert"
                                                        disabled={(outboundCall.destination || "").length === 0 || (outboundCall.caller_prefix || "").length === 0}
                                                        onClick={() => {
                                                            if ((outboundCall.destination || "").length === 0 || (outboundCall.caller_prefix || "").length === 0) return;
                                                            save_alert();
                                                        }}
                                                        active={buttonActive.save_alert}
                                                        small
                                                        hideContainer
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xxl-2 col-lg-3 pl-0" style={{ overflowY: "auto" }}>
                            {status !== "ringing" ? (
                                <></>
                            ) : (
                                <div id="vca-right">
                                    <div id="vca-right-phone-inbound" className="d-none">
                                        {/* <div className="small">Client Name</div>
                                        <div className="fw-600">{activeClient?.name || "-"}</div> */}
                                        {/* <div className="small mt-2">Caller Name</div>
                                        <div className="fw-600">{inboundCallRef.current?.caller_name || "-"}</div>
                                        <div className="small mt-2">CLI</div>
                                        <div className="fw-600">
                                            {(activeClient.dids || []).map((a: any, i: number) => {
                                                if (a.did === inboundCallRef.current.did && Number(a.hide_cli) === 0) {
                                                    return inboundCallRef.current.from;
                                                }
                                                return <></>;
                                            })}
                                        </div> */}
                                        {/* 
                                        OLD REPLACED
                                        <Button
                                            color="green"
                                            onClick={() => {
                                                inboundCallRef.current.twilio_active_call.accept();
                                            }}
                                            value="Accept call"
                                            className="w-100 mt-2"
                                            hideContainer
                                        /> */}
                                    </div>
                                    {/* */}
                                    <div className="col-12">
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="small">Client Name</div>
                                                <div dangerouslySetInnerHTML={{ __html: activeClient.name }}></div>
                                                <div dangerouslySetInnerHTML={{ __html: `<b>${activeClient?.service_type}</b>` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="col-12">
                                        <div className="row">
                                            <div className="col-12">
                                                <Button
                                                    className="w-100 mt-2"
                                                    color="green"
                                                    hideContainer
                                                    value="Accept Call"
                                                    onClick={async () => {
                                                        answer_call_v2();
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="col-12">
                                        {" "}
                                        <div className={statusRef.current !== "inbound" ? "row" : "d-none"}>
                                            <div className="col-lg-12">
                                                <div>
                                                    <div className="small green">Answer Script</div>
                                                    <div>
                                                        {(() => {
                                                            // If no DIDs available, return empty string
                                                            if (!activeClient?.dids?.length) return "";

                                                            // If not in the right call status or no call data, return the first DID's welcome script
                                                            if (!["ringing", "inbound"].includes(status) || JSON.stringify(inboundCallRef.current || outboundCall) === "{}") {
                                                                return activeClient?.dids?.[0]?.welcome_script || "";
                                                            }

                                                            // Find the correct DID safely
                                                            const currentDid = inboundCallRef.current?.did || outboundCall?.did;
                                                            const didIndex = activeClient.dids.findIndex((a: any) => a.did === currentDid);

                                                            // Get the DID object (or fallback to the first one if not found)
                                                            const did = didIndex >= 0 ? activeClient.dids[didIndex] : activeClient.dids[0];

                                                            // Get timezone safely
                                                            const timezone = did?.tz || "Australia/Sydney";

                                                            // Get time of day
                                                            const hour = moment.tz(timezone).hour();
                                                            const timeOfDay = hour < 12 ? "morning" : "afternoon";

                                                            // Return the formatted welcome script
                                                            return he
                                                                .decode(did?.welcome_script || "")
                                                                .replace("<agent_name>", user.first_name)
                                                                .replace("<time_of_day>", timeOfDay);
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/*  */}

                                    <div className="col-12">
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="small mt-2">Caller Name</div>
                                                <div className="--fw-600">{inboundCallRef.current?.caller_name || "-"}</div>
                                                <div className="small mt-2">Callers Number</div>
                                                <div className="--fw-600">
                                                    {(activeClient.dids || []).map((a: any, i: number) => {
                                                        if (a.did === inboundCallRef.current.did && Number(a.hide_cli) === 0) {
                                                            return inboundCallRef.current.from;
                                                        }
                                                        return <></>;
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {["ringing", ""].includes(statusRef.current) ? (
                                <></>
                            ) : (
                                <div id="vca-right">
                                    <div
                                        className="row"
                                        style={{
                                            border: "none",
                                            backgroundColor: "transparent",
                                            padding: 0,
                                            marginBottom: "15px",
                                        }}>
                                        {/*  */}
                                        <div className={["search", "wrapping up"].includes(statusRef.current) ? "col-lg-12 text-right" : "d-none"}>
                                            {["wrapping up"].includes(statusRef.current) ? (
                                                <>
                                                    <Button
                                                        value="Close Client Info & Close Screen"
                                                        active={buttonActive.call_action["wrapping up done"]}
                                                        small
                                                        color="red"
                                                        className="w-100"
                                                        onClick={() => {
                                                            if (messageSent !== true) {
                                                                const a = window.confirm("Are you sure you want to close screen without sending a message?");
                                                                if (a) {
                                                                    call_action("wrapping up done");
                                                                }
                                                                return;
                                                            }
                                                            call_action("wrapping up done");
                                                        }}
                                                    />

                                                    <div className={(inboundCallRef.current.participant_caller_call_sid || outboundCall.participant_caller_call_sid) && messageSent !== true && endCallSeconds < 20 ? "row" : "d-none"}>
                                                        <div className="col-12">
                                                            <Button
                                                                value="No Caller & Close Screen"
                                                                active={buttonActive.call_action["no caller"]}
                                                                small
                                                                color="blue"
                                                                className="w-100"
                                                                onClick={() => {
                                                                    call_action("no caller", { did: inboundCallRef.current.did, conf_sid: inboundCallRef.current.conference_sid });
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        color="red"
                                                        small
                                                        hideContainer
                                                        value="Close client info / close screen"
                                                        onClick={() => {
                                                            setActiveClientId(0);
                                                            setActiveClient({});
                                                            setSendMessage({});
                                                            setConditionalFormAnswers([]);
                                                            setCustomerAlerts([]);
                                                            // setOutboundCall({ ...outboundCall, caller_prefix: "+61" });
                                                            // outboundCallRef.current = {
                                                            //     ...outboundCall,
                                                            //     caller_prefix: "+61",
                                                            // };
                                                            setOutboundCall({ caller_prefix: "+61" });
                                                            outboundCallRef.current = {
                                                                caller_prefix: "+61",
                                                            };
                                                            setStatus("");
                                                            statusRef.current = "";
                                                            setMessageSent({});
                                                            activeClientIdRef.current = 0;
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                        {/*  */}
                                    </div>
                                    {/* */}
                                    <div className={activeClientIdRef.current === 0 ? "d-none" : "col-12"}>
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="small">Client Name</div>
                                                <div dangerouslySetInnerHTML={{ __html: activeClient.name }}></div>
                                                <div dangerouslySetInnerHTML={{ __html: `<b>${activeClient?.service_type}</b>` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                    {/*  */}
                                    {["inbound", "wrapping up"].includes(statusRef.current) ? (
                                        statusRef.current === "wrapping up" && activeClientIdRef.current === 0 ? (
                                            <></>
                                        ) : (
                                            <div className="col-12">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <div className="small mt-2">Caller Name</div>
                                                        <div className="--fw-600">{inboundCallRef.current?.caller_name || "-"}</div>
                                                        <div className="small mt-2">Callers Number</div>
                                                        <div className="--fw-600">
                                                            {(activeClient.dids || []).map((a: any, i: number) => {
                                                                if (a.did === inboundCallRef.current.did && Number(a.hide_cli) === 0) {
                                                                    return inboundCallRef.current.from;
                                                                }
                                                                return <></>;
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    ) : (
                                        <></>
                                    )}
                                    {/*  */}
                                    <div className={!["wrapping up"].includes(statusRef.current) ? "col-12" : "d-none"}>
                                        {" "}
                                        <div className="row">
                                            <div className="col-lg-12">
                                                <div>
                                                    <div className="small green">Answer Script</div>
                                                    <div>
                                                        {(() => {
                                                            // If no DIDs available, return empty string
                                                            if (!activeClient?.dids?.length) return "";

                                                            // If not in the right call status or no call data, return the first DID's welcome script
                                                            if (!["ringing", "inbound"].includes(status) || JSON.stringify(inboundCallRef.current || outboundCall) === "{}") {
                                                                return activeClient?.dids?.[0]?.welcome_script || "";
                                                            }

                                                            // Find the correct DID safely
                                                            const currentDid = inboundCallRef.current?.did || outboundCall?.did;
                                                            const didIndex = activeClient.dids.findIndex((a: any) => a.did === currentDid);

                                                            // Get the DID object (or fallback to the first one if not found)
                                                            const did = didIndex >= 0 ? activeClient.dids[didIndex] : activeClient.dids[0];

                                                            // Get timezone safely
                                                            const timezone = did?.tz || "Australia/Sydney";

                                                            // Get time of day
                                                            const hour = moment.tz(timezone).hour();
                                                            const timeOfDay = hour < 12 ? "morning" : "afternoon";

                                                            // Return the formatted welcome script
                                                            return he
                                                                .decode(did?.welcome_script || "")
                                                                .replace("<agent_name>", user.first_name)
                                                                .replace("<time_of_day>", timeOfDay);
                                                        })()}
                                                        {/* {(activeClient.dids || []).length > 0
                                                        ? ["ringing", "inbound"].includes(status) && JSON.stringify(inboundCallRef.current || outboundCall) !== "{}"
                                                            ? he
                                                                  .decode(activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === (inboundCallRef.current.did || outboundCall.did))]?.welcome_script || "")
                                                                  .replace("<agent_name>", user.first_name)
                                                                  .replace(
                                                                      "<time_of_day>",
                                                                      Number(
                                                                          moment.tz(activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === (inboundCallRef.current.did || outboundCall.did))].tz || "Australia/Sydney").hour()
                                                                      ) < 12
                                                                          ? "morning"
                                                                          : "afternoon"
                                                                  )
                                                            : activeClient?.dids?.[0]?.welcome_script
                                                        : ""} */}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div id="vca-right-phone-inbound" className={["wrapping up"].includes(statusRef.current) ? "d-none" : ""}>
                                        <div className={!["inbound", "outbound"].includes(status) ? "d-none" : "row"}>
                                            <div className="col-12">
                                                <h4 className="text-center mb-2">
                                                    {status === "inbound"
                                                        ? (activeClient.dids || []).map((a: any, i: number) => {
                                                              if (a.did === inboundCallRef.current.did && Number(a.hide_cli) === 0) {
                                                                  return inboundCallRef.current.from;
                                                              }
                                                              return <></>;
                                                          })
                                                        : outboundCall.to}
                                                </h4>
                                                <div className="text-center small mb-4">{timerCallDuration.string}</div>
                                            </div>
                                            <div className="col-12 p-0">
                                                <div className="small">Call participants</div>
                                                <div className="call-participants-card mb-1">
                                                    <table className="w-100">
                                                        <tbody>
                                                            <tr>
                                                                <td className="fw-600">Caller</td>
                                                                <td>{_he_rounds(status === "inbound" ? inboundCallRef.current.caller_name || "-" : outboundCall.caller_name || "-")}</td>
                                                                <td>{timerCallDuration.string}</td>
                                                                <td></td>
                                                            </tr>
                                                            <tr>
                                                                <td className="fw-600">Agent</td>
                                                                <td>{_he_rounds(user.first_name || "-")}</td>
                                                                <td>{timerAgentCallDuration.string}</td>
                                                                <td>
                                                                    {buttonActive.call_action.mute ? (
                                                                        <>
                                                                            <Loader type="circle" />
                                                                        </>
                                                                    ) : (
                                                                        <div
                                                                            className="fake-href"
                                                                            onClick={() => {
                                                                                if (buttonActive.call_action.mute) return;
                                                                                call_action("mute v2", {
                                                                                    mute: Number((status === "inbound" ? inboundCallRef.current.participant_agent_muted : outboundCall.participant_agent_muted) || 0) === 1 ? 0 : 1,
                                                                                    participant: "agent",
                                                                                });
                                                                            }}>
                                                                            {Number(status === "inbound" ? inboundCallRef.current.participant_agent_muted : outboundCall.participant_agent_muted) === 1 ? "Unmute" : "Mute"}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            {status === "inbound" ? (
                                                                <tr className={[1, 2].includes(Number(inboundCallRef.current.participant_client_status || 0)) ? "" : "d-none"}>
                                                                    <td className="fw-600">Client</td>
                                                                    <td></td>
                                                                    <td>{Number(inboundCallRef.current.participant_client_status) === 2 ? "Ringing" : timerClientCallDuration.string}</td>
                                                                    <td></td>
                                                                </tr>
                                                            ) : (
                                                                <tr className={[1, 2].includes(Number(outboundCall.participant_client_status || 0)) ? "" : "d-none"}>
                                                                    <td className="fw-600">Client</td>
                                                                    <td></td>
                                                                    <td>{Number(outboundCall.participant_client_status) === 2 ? "Ringing" : timerClientCallDuration.string}</td>
                                                                    <td></td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div className="col-12 p-0">
                                                    <Button
                                                        hideContainer
                                                        value={
                                                            (inboundCallRef.current.participant_client_status || outboundCallRef.current.participant_client_status || 0) === 2
                                                                ? "Calling Client"
                                                                : (inboundCallRef.current.participant_client_status || outboundCallRef.current.participant_client_status || 0) === 1
                                                                  ? "Talking to Client"
                                                                  : (inboundCallRef.current.participant_caller_status || outboundCallRef.current.participant_caller_status || 0) === 1
                                                                    ? "Talking to Caller"
                                                                    : "No status"
                                                        }
                                                        small
                                                        color="green"
                                                        className="w-100"
                                                        onClick={() => {}}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* <div className={!["wrapping up"].includes(status) ? "d-none" : "row"}>
                                            <div className="col-12 p-0">
                                                <Button
                                                    value="Close screen"
                                                    active={buttonActive.call_action["wrapping up done"]}
                                                    small
                                                    color="red"
                                                    className="w-100"
                                                    onClick={() => {
                                                        if (messageSent !== true) {
                                                            const a = window.confirm("Are you sure you want to close screen without sending a message?");
                                                            if (a) {
                                                                call_action("wrapping up done");
                                                            }
                                                            return;
                                                        }
                                                        call_action("wrapping up done");
                                                    }}
                                                />
                                            </div>
                                        </div> */}

                                        <div className={!["inbound", "outbound"].includes(status) ? "d-none" : "row"}>
                                            {showKeyPad ? (
                                                <div id="vca-right-keypad" className="col-12 p-0">
                                                    <div className="row">
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("1")}>1</span>
                                                        </div>
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("2")}>2</span>
                                                        </div>
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("3")}>3</span>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("4")}>4</span>
                                                        </div>
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("5")}>5</span>
                                                        </div>
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("6")}>6</span>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("7")}>7</span>
                                                        </div>
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("8")}>8</span>
                                                        </div>
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("9")}>9</span>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("*")}>*</span>
                                                        </div>
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("0")}>0</span>
                                                        </div>
                                                        <div className="col-4">
                                                            <span onClick={() => handleSendDigit("#")}>#</span>
                                                        </div>
                                                        <div className="col-12 p-0 mb-4">
                                                            <Button
                                                                value="Hide keypad"
                                                                small
                                                                className="w-100"
                                                                onClick={() => {
                                                                    setShowKeyPad(false);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="col-12 p-0">
                                                        <Button
                                                            value="Show keypad"
                                                            small
                                                            className="w-100"
                                                            onClick={() => {
                                                                setShowKeyPad(true);
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-12 p-0">
                                                        <Button
                                                            value="End call"
                                                            small
                                                            color="red"
                                                            className="w-100"
                                                            disabled={statusRef.current === "outbound" && (outboundCall?.twilio_active_call?.parameters?.CallSid || "").length === 0}
                                                            onClick={async () => {
                                                                if (statusRef.current === "outbound") {
                                                                    outboundCall.twilio_active_call.disconnect();
                                                                    force_end_call(outboundCall.twilio_active_call.parameters.CallSid);

                                                                    const params = new URLSearchParams(window.location.search);
                                                                    const bhvm_sid = params.get("bhvm_sid");

                                                                    params.delete("bhvm_sid");

                                                                    window.history.replaceState(null, "", window.location.pathname);
                                                                    await new fetch(`/user/vca/bhvm-start-call?call_sid=${bhvm_sid}&action=close`, {}, { success: false, error: true }).get();
                                                                } else {
                                                                    setEndCallSeconds(Number((new Date().getTime() - timerCallDuration.ts) / 1000));
                                                                    inboundCallRef.current.twilio_active_call.disconnect();
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className={["inbound", "outbound", "ringing"].includes(statusRef.current) ? "row" : "d-none"}>
                                            {/* <div className="col-3 px-1">
                                                <div
                                                    className={
                                                        status === "inbound"
                                                            ? JSON.stringify(inboundCallRef.current) !== "{}" && inboundCallRef.current?.last_call_attempt
                                                                ? "call-action-btn-active"
                                                                : "call-action-btn-inactive"
                                                            : status === "outbound"
                                                            ? JSON.stringify(outboundCall) !== "{}" && outboundCall?.last_call_attempt
                                                                ? "call-action-btn-active"
                                                                : "call-action-btn-inactive"
                                                            : "call-action-btn-inactive"
                                                    }
                                                    onClick={() => {
                                                        if (!["inbound", "outbound"].includes(status)) return;
                                                        if (status === "inbound" && !inboundCallRef.current?.last_call_attempt) return;
                                                        if (status === "outbound" && !outboundCall?.last_call_attempt) return;

                                                        call_action(status === "inbound" ? "make call v2" : "make call outbound", {
                                                            phone_number: status === "inbound" ? inboundCallRef.current.last_call_attempt : outboundCall.last_call_attempt,
                                                        });
                                                    }}
                                                >
                                                    {buttonActive.call_action[status === "inbound" ? "make call v2" : "make call outbound"] ? <Loader type="circle" /> : "Call client"}
                                                </div>
                                            </div> */}
                                            <div className="col-4 px-1">
                                                <div
                                                    className={
                                                        status === "inbound"
                                                            ? JSON.stringify(inboundCallRef.current) !== "{}" &&
                                                              Number(inboundCallRef.current?.participant_caller_status || 0) === 1 &&
                                                              //   Number(inboundCallRef.current?.participant_agent_status || 0) === 1 &&
                                                              Number(inboundCallRef.current?.participant_client_status || 0) === 1
                                                                ? "call-action-btn-active"
                                                                : "call-action-btn-inactive"
                                                            : status === "outbound"
                                                              ? JSON.stringify(outboundCall) !== "{}" &&
                                                                Number(outboundCall?.participant_caller_status || 0) === 1 &&
                                                                //   Number(outboundCall?.participant_agent_status || 0) === 1 &&
                                                                Number(outboundCall?.participant_client_status || 0) === 1
                                                                  ? "call-action-btn-active"
                                                                  : "call-action-btn-inactive"
                                                              : "call-action-btn-inactive"
                                                    }
                                                    onClick={() => {
                                                        if (!["inbound", "outbound"].includes(status)) return;
                                                        if (status === "inbound")
                                                            if (
                                                                Number(inboundCallRef.current?.participant_caller_status || 0) === 0 ||
                                                                // Number(inboundCallRef.current?.participant_agent_status || 0) === 0 ||
                                                                Number(inboundCallRef.current?.participant_client_status || 0) === 0
                                                            )
                                                                return;
                                                        if (status === "outbound")
                                                            if (
                                                                Number(outboundCall?.participant_caller_status || 0) === 0 ||
                                                                // Number(outboundCall?.participant_agent_status || 0) === 0 ||
                                                                Number(outboundCall?.participant_client_status || 0) === 0
                                                            )
                                                                return;

                                                        call_action("merge call v2");
                                                    }}>
                                                    {buttonActive.call_action["merge call v2"] ? <Loader type="circle" /> : "Merge calls"}
                                                </div>
                                            </div>
                                            <div className="col-4 px-1">
                                                <div
                                                    className={
                                                        status === "inbound"
                                                            ? JSON.stringify(inboundCallRef.current) !== "{}" &&
                                                              Number(inboundCallRef.current?.participant_caller_hold || 0) === 1 &&
                                                              Number(inboundCallRef.current?.participant_client_status) === 0
                                                                ? "call-action-btn-active"
                                                                : "call-action-btn-inactive"
                                                            : status === "outbound"
                                                              ? JSON.stringify(outboundCall) !== "{}" && Number(outboundCall?.participant_caller_hold || 0) === 1
                                                                  ? "call-action-btn-active"
                                                                  : "call-action-btn-inactive"
                                                              : "call-action-btn-inactive"
                                                    }
                                                    onClick={() => {
                                                        if (!["inbound", "outbound"].includes(status)) return;
                                                        if (status === "inbound" && Number(inboundCallRef.current?.participant_caller_hold || 0) === 0 && Number(inboundCallRef.current?.participant_client_status) === 0) return;
                                                        if (status === "outbound" && Number(outboundCall?.participant_caller_hold || 0) === 0) return;
                                                        call_action("hold v2", { participant: "caller", hold: 0 });
                                                    }}>
                                                    {buttonActive.call_action["hold"] ? <Loader type="circle" /> : "Return to caller"}
                                                </div>
                                            </div>
                                            <div className="col-4 px-1">
                                                <div
                                                    className={
                                                        status === "inbound"
                                                            ? JSON.stringify(inboundCallRef.current) !== "{}" && Number(inboundCallRef.current?.participant_client_status || 0) !== 0
                                                                ? //   &&Number(inboundCallRef.current?.participant_agent_status || 0) === 1
                                                                  "call-action-btn-active"
                                                                : "call-action-btn-inactive"
                                                            : status === "outbound"
                                                              ? JSON.stringify(outboundCall) !== "{}" && Number(outboundCall?.participant_client_status || 0) !== 0
                                                                  ? // && Number(outboundCall?.participant_agent_status || 0) === 1
                                                                    "call-action-btn-active"
                                                                  : "call-action-btn-inactive"
                                                              : "call-action-btn-inactive"
                                                    }
                                                    onClick={() => {
                                                        if (!["inbound", "outbound"].includes(status)) return;
                                                        if (status === "inbound") if (Number(inboundCallRef.current?.participant_client_status || 0) === 0) return;
                                                        // || Number(inboundCallRef.current?.participant_agent_status || 0) === 0) return;
                                                        if (status === "outbound") if (Number(outboundCall?.participant_client_status || 0) === 0) return;
                                                        // || Number(outboundCall?.participant_agent_status || 0) === 0) return;

                                                        call_action("drop participant v2", { participant: "client" });
                                                    }}>
                                                    {buttonActive.call_action["drop participant"] ? <Loader type="circle" /> : "Hang up client"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* {["inbound"].includes(statusRef.current) ? (
                                        <div className="col-12">
                                            <div className="row">
                                                <div className="col-12">
                                                    <div className="small mt-2">Caller Name</div>
                                                    <div className="--fw-600">{inboundCallRef.current?.caller_name || "-"}</div>
                                                    <div className="small mt-2">Callers Number</div>
                                                    <div className="--fw-600">
                                                        {(activeClient.dids || []).map((a: any, i: number) => {
                                                            if (a.did === inboundCallRef.current.did && Number(a.hide_cli) === 0) {
                                                                return inboundCallRef.current.from;
                                                            }
                                                            return <></>;
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <></>
                                    )} */}
                                    <div id="vca-right-phone-outbound" className={["ringing", "inbound", "wrapping up", "outbound"].includes(status) ? "d-none" : ""}>
                                        <div className="fw-400">Outbound</div>
                                        <div>
                                            <div className="row">
                                                <div className="col-3 pr-0">
                                                    <Select
                                                        container={false}
                                                        options={(Number(user.user_id) === 1 ? ["+61", "+1", "+63", "+44", "+971"] : ["+61", "+1", "+63", "+44"]).map((a: any) => {
                                                            return { label: a, value: a };
                                                        })}
                                                        name="outboundprefix"
                                                        placeholder="prefix"
                                                        className="input-small w-100"
                                                        hideLabel
                                                        defaultValue={outboundCall.caller_prefix}
                                                        updateChange={(v) => {
                                                            setOutboundCall({ ...outboundCall, caller_prefix: v });
                                                            outboundCallRef.current = {
                                                                ...outboundCall,
                                                                caller_prefix: v,
                                                            };
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-9">
                                                    <Input
                                                        container={false}
                                                        className="input-small"
                                                        name="destination"
                                                        placeholder="Destination"
                                                        type="text"
                                                        defaultValue={outboundCall.destination}
                                                        changeDefault={new Date().getTime()}
                                                        updateChange={(v) => {
                                                            setOutboundCall({ ...outboundCall, destination: v });
                                                            outboundCallRef.current = {
                                                                ...outboundCall,
                                                                destination: v,
                                                            };
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <Select
                                                options={(activeClient?.dids || []).map((a: any) => {
                                                    if (a.did.substring(0, 7) === "+611300") return;
                                                    return { label: a.did, value: a.did };
                                                })}
                                                name="outbounddid"
                                                placeholder="DID"
                                                className="input-small"
                                                defaultValue={outboundCall.did || ""}
                                                updateChange={(v) => {
                                                    setOutboundCall({ ...outboundCall, did: v });
                                                    outboundCallRef.current = {
                                                        ...outboundCall,
                                                        did: v,
                                                    };
                                                }}
                                            />

                                            {(customerAlerts || []).length === 0 ? (
                                                <></>
                                            ) : (
                                                <>
                                                    {(customerAlerts || []).map((a: any, i: number) => {
                                                        if (a.alert?.alert === null || a.alert === null || Number(a.status) !== 1) return <React.Fragment key={i} />;
                                                        return (
                                                            <div key={i} className="light-red-bg p-2 small mb-1">
                                                                <table>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="vertical-align-top">
                                                                                <b>Name</b>
                                                                            </td>
                                                                            <td>
                                                                                {a.first_name} {a.lsst_name}
                                                                            </td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="vertical-align-top">
                                                                                <b>Alert</b>
                                                                            </td>
                                                                            <td dangerouslySetInnerHTML={{ __html: he.decode(a.alert.alert || "") }}></td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}

                                            <Button
                                                className="w-100"
                                                hideContainer
                                                small
                                                active={buttonActive.check_customer_alerts}
                                                disabled={(outboundCall.destination || "").length === 0}
                                                value="Check customer alerts"
                                                onClick={() => {
                                                    check_customer_alerts(outboundCall.caller_prefix + outboundCall.destination);
                                                }}
                                            />
                                            <div className="row">
                                                <div className="col-12">
                                                    <Button
                                                        className="w-100 mt-2"
                                                        color="blue"
                                                        hideContainer
                                                        small
                                                        value="Call (Non-billable)"
                                                        disabled={(outboundCall?.destination || "").length === 0 || (outboundCall?.did || "").length === 0 || buttonActive?.outbound}
                                                        onClick={async () => {
                                                            make_outbound("false");
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-12">
                                                    <Button
                                                        className="w-100 mt-2"
                                                        color="green"
                                                        hideContainer
                                                        small
                                                        value="Call (billable)"
                                                        disabled={(outboundCall?.destination || "").length === 0 || (outboundCall?.did || "").length === 0 || buttonActive?.outbound}
                                                        onClick={async () => {
                                                            make_outbound("true");
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div ref={(_) => vca_right_contact(_)} id="vca-right-contacts">
                                        <div className="fw-400">Contacts</div>
                                        <Input
                                            container={false}
                                            className="input-small"
                                            name="Searchcontacts"
                                            placeholder="Search contacts"
                                            type="text"
                                            updateChange={(v) => {
                                                setSearchContacts(v);
                                            }}
                                        />
                                        {(() => {
                                            // Only filter if conditional forms are enabled
                                            const isConditionalFormEnabled = Number(activeClient?.conditional_form?.status || 0) === 1;

                                            let contactsToShow = activeClient?.contacts || [];

                                            // console.log("-".repeat(50));
                                            // console.log(Number(activeClient?.conditional_form?.status || 0));
                                            // console.log("/".repeat(50));
                                            // console.log(contactsToShow);
                                            // console.log("+".repeat(50));

                                            if (isConditionalFormEnabled) {
                                                if (currentQuestionContacts.length === 0) {
                                                    // No contacts assigned to current question
                                                    return <div className="text-muted small mt-3">No contacts assigned to this question</div>;
                                                }

                                                // Filter contacts based on current question's contact UUIDs
                                                // De-duplicate by uuid and maintain original order
                                                const seenUuids = new Set<string>();
                                                contactsToShow = contactsToShow.filter((contact: any) => {
                                                    if (currentQuestionContacts.includes(contact.uuid) && !seenUuids.has(contact.uuid)) {
                                                        seenUuids.add(contact.uuid);
                                                        return true;
                                                    }
                                                    return false;
                                                });
                                            }

                                            // Render filtered contacts
                                            return contactsToShow.map((a: any, i: number) => {
                                                if ((a?.first_name || "").toLowerCase().includes((searchContacts || "").toLowerCase()) || (a?.last_name || "").toLowerCase().includes((searchContacts || "").toLowerCase())) {
                                                    return (
                                                        <div className={`${Number(a.index) === activeTransfer ? "vca-right-contacts-container-active" : ""} vca-right-contacts-container`} key={i}>
                                                            <div className="fw-400">
                                                                {a.first_name} {a.last_name}
                                                            </div>
                                                            <div>{a.job_title || "-"}</div>
                                                            <div>{a.short_description || "-"}</div>
                                                            <div className="row mt-2">
                                                                <div className="col-6 pr-1">
                                                                    <Button
                                                                        className="w-100"
                                                                        value={a.email ? "Message" : "No Email"}
                                                                        hideContainer
                                                                        small
                                                                        disabled={!a.email}
                                                                        onClick={() => {
                                                                            if (a.email) {
                                                                                setSendMessage({ ...sendMessage, to_name: a.first_name + " " + a.last_name, to_email: a.email, soucre: "email", to_sms: a.mobile });
                                                                            }
                                                                        }}
                                                                    />{" "}
                                                                </div>
                                                                <div className={a.preferred_phone ? "col-6 pl-1" : "d-none"}>
                                                                    <Button
                                                                        className="w-100"
                                                                        value="Transfer"
                                                                        active={buttonActive.call_action[status === "outbound" ? "make call outbound" + a.index : "make call v2" + a.index]}
                                                                        hideContainer
                                                                        small
                                                                        onClick={() => {
                                                                            setActiveTransfer(a.index);
                                                                            call_action(status === "outbound" ? "make call outbound" : "make call v2", { phone_number: a.preferred_phone, btn_index: a.index });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            });
                                        })()}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Home;
